/**
 * Product Display Manager
 * Handles all product filtering, rendering and display logic
 * Following SOLID principles for clean separation of concerns
 */

const MULTI_FORMAT_SELECTION_WARNING_HTML =
  (typeof window !== 'undefined' &&
    window.ProductSystem?.MULTI_FORMAT_SELECTION_WARNING_MARKUP) ||
  '<div class="multi-format-selection-warning" hidden role="status">Heads up! You have selected more than one format for this title.</div>';

class ProductDisplayManager {
  /**
   * @param {Object} config - Configuration object
   * @param {Array<any>} config.products - Products array
   * @param {Array<any>} config.ddcsSection - DDCS section data
   * @param {Array<any>} config.ddcsCampusEntity - Campus entities
   * @param {Array<any>} config.ddcsTerm - Terms data
   * @param {any} config.productHandler - Product handler instance
   */
  constructor(config) {
    this.products = config.products || [];
    this.ddcsSection = config.ddcsSection || [];
    this.ddcsCampusEntity = config.ddcsCampusEntity || [];
    this.ddcsTerm = config.ddcsTerm || [];
    this.productHandler = config.productHandler;

    // Cache DOM elements
    this.globalProductsContainer = null;
    /** @type {number} Incremented each refresh so stale async passes cannot repaint DOM */
    this._refreshGeneration = 0;

    // Bind methods
    this.refreshProductsDisplay = this.refreshProductsDisplay.bind(this);
    this.displayDepartmentSection = this.displayDepartmentSection.bind(this);
    this.displayAdoptionStatusCards =
      this.displayAdoptionStatusCards.bind(this);
    this.displayNormalProductSections =
      this.displayNormalProductSections.bind(this);
    this.getProductsContainer = this.getProductsContainer.bind(this);

    // Initialize adoption status configuration
    this.initializeAdoptionConfig();

  }

  /**
   * Initialize adoption status configuration
   * @private
   */
  initializeAdoptionConfig() {
    this.ADOPTION_STATUS_CONFIG = {
      cancelled: {
        keywords: ['cancelled', 'cancel'],
      },
      'open-resources': {
        keywords: ['open educational resources', 'free'],
      },
      'not-required': {
        keywords: ["aren't required", 'not required'],
      },
      pending: {
        keywords: ['being finalized', 'determined', 'check back'],
      },
    };

    this.ADOPTION_STATUS_ICON =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/><path d="M12 8v4M12 16h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  /**
   * Get or cache the products container element
   * @returns {HTMLElement|null} Products container
   */
  getProductsContainer() {
    if (!this.globalProductsContainer) {
      this.globalProductsContainer =
        document.getElementById('filtered-products');
    }
    return this.globalProductsContainer;
  }
  /**
   * @param {any} metaobjectType
   * @param {any} query
   */
  async fetchMetaobjects(metaobjectType, query) {
    if (typeof window !== 'undefined' && window.fetchMetaobjectsOnce) {
      return window.fetchMetaobjectsOnce(metaobjectType, query);
    }

    const proxyPath = '/apps/shopifyData/metaobjects';
    const url = `${proxyPath}?type=${metaobjectType}&query=${query}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        const activeMetaobjects = result.data.filter(
          // @ts-ignore
          item => item.status === 'ACTIVE',
        );
        return activeMetaobjects;
      } else {
        throw new Error(result.error || 'Failed to fetch metaobjects');
      }
    } catch (error) {
      console.error('Error fetching metaobjects:', error);
      throw error;
    }
  }
  /**
   * @param {any} queryData
   */
  async fetchProducts(queryData) {
    if (typeof window !== 'undefined' && window.fetchProductsOnce) {
      return window.fetchProductsOnce(queryData);
    }

    const proxyPath = '/apps/shopifyData/products';
    const url = `${proxyPath}?query=${queryData}`;

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        return result.products;
      } else {
        throw new Error(result.error || 'Failed to fetch products');
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  }
  /**
   * Stable id for deduping product rows (parallel fetches / duplicate filters).
   * @param {any} product
   * @returns {string}
   */
  static _productDedupeId(product) {
    if (!product) return '';
    return String(
      product.id ||
        product.gid ||
        product.admin_graphql_api_id ||
        product.product_id ||
        ''
    );
  }

  /**
   * @param {any} product
   * @returns {Array<Record<string, unknown>>}
   */
  static _parseSectionDataMapping(product) {
    const raw = product?.metafields?.course_materials?.section_data_mapping;
    if (!raw) return [];
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {any} product
   * @returns {string[]}
   */
  static _productDdcsSectionGids(product) {
    /** @type {string[]} */
    const gids = [];
    const mf = product?.metafields;
    const candidates = [
      mf?.ddcs_section,
      mf?.ddcs?.ddcs_section,
      mf?.course_materials?.ddcs_section,
    ];
    for (const candidate of candidates) {
      if (!candidate) continue;
      if (typeof candidate === 'string') {
        try {
          const parsed = JSON.parse(candidate);
          if (Array.isArray(parsed)) {
            parsed.forEach(x => gids.push(String(x)));
          } else {
            gids.push(String(candidate));
          }
        } catch {
          gids.push(String(candidate));
        }
      } else if (Array.isArray(candidate)) {
        candidate.forEach(x => {
          if (typeof x === 'string') gids.push(x);
          else if (x?.gid) gids.push(String(x.gid));
        });
      }
    }
    return gids;
  }

  /**
   * Post-fetch filter: product section labels vs filter.selectedsection.
   * @param {any} product
   * @param {{ selectedsection?: string, selectedsectionId?: string, selectedsectionGid?: string }} filter
   * @returns {boolean}
   */
  static _productSectionMatchesFilter(product, filter) {
    if (!product || !filter) return false;
    const sectionLabel = String(filter.selectedsection ?? '').trim();
    if (!sectionLabel) return true;

    const norm = ProductDisplayManager._normalizeCmrpCompareKey;
    if (norm(product._cmrpFetchSectionLabel) === norm(sectionLabel)) return true;

    const sections = Array.isArray(product.section) ? product.section : [];
    if (sections.some(s => norm(s) === norm(sectionLabel))) return true;

    const sectionId = String(filter.selectedsectionId ?? '').trim();
    if (sectionId) {
      if (String(product._cmrpFetchSectionId ?? '') === sectionId) return true;
      const mapping = ProductDisplayManager._parseSectionDataMapping(product);
      if (mapping.some(m => String(m?.SectionID ?? '') === sectionId)) return true;
    }

    const sectionGid = String(filter.selectedsectionGid ?? '').trim();
    if (
      sectionGid &&
      norm(product._cmrpFetchSectionGid) === norm(sectionGid)
    ) {
      return true;
    }

    return false;
  }

  /**
   * Row gate: attach products to the correct section card (sectionId / GID / mapping).
   * @param {any} product
   * @param {{ selectedsection?: string, selectedsectionId?: string, selectedsectionGid?: string }} courseInfo
   * @returns {boolean}
   */
  static _productRowMatchesCourseSectionIds(product, courseInfo) {
    if (!product) return false;
    const sectionIdNeedle = String(courseInfo?.selectedsectionId ?? '').trim();
    const sectionGidNeedle = String(courseInfo?.selectedsectionGid ?? '').trim();
    if (!sectionIdNeedle && !sectionGidNeedle) return true;

    const norm = ProductDisplayManager._normalizeCmrpCompareKey;
    const fetchId = String(product._cmrpFetchSectionId ?? '').trim();
    const fetchGid = String(product._cmrpFetchSectionGid ?? '').trim();

    if (fetchId || fetchGid) {
      if (
        sectionIdNeedle &&
        fetchId &&
        (fetchId === sectionIdNeedle || fetchId.includes(sectionIdNeedle))
      ) {
        return true;
      }
      if (
        sectionGidNeedle &&
        fetchGid &&
        norm(fetchGid) === norm(sectionGidNeedle)
      ) {
        return true;
      }
      return false;
    }

    const sectionIds = product?.sectionId;
    if (sectionIds != null && sectionIds !== '') {
      if (Array.isArray(sectionIds)) {
        if (
          sectionIdNeedle &&
          sectionIds.some(id => String(id).includes(sectionIdNeedle))
        ) {
          return true;
        }
      } else if (
        sectionIdNeedle &&
        String(sectionIds).includes(sectionIdNeedle)
      ) {
        return true;
      }
    }

    if (sectionIdNeedle) {
      const mapping = ProductDisplayManager._parseSectionDataMapping(product);
      if (mapping.some(m => String(m?.SectionID ?? '') === sectionIdNeedle)) {
        return true;
      }
    }

    if (sectionGidNeedle) {
      const gids = ProductDisplayManager._productDdcsSectionGids(product);
      if (gids.some(g => norm(g) === norm(sectionGidNeedle))) return true;
    }

    const hasSectionIds =
      sectionIds != null &&
      sectionIds !== '' &&
      (!Array.isArray(sectionIds) || sectionIds.length > 0);
    if (!hasSectionIds) {
      return ProductDisplayManager._productSectionMatchesFilter(product, courseInfo);
    }

    return ProductDisplayManager._productSectionMatchesFilter(product, courseInfo);
  }

  /**
   * Shallow-clone products and stamp the filter section they were fetched for.
   * @param {Array<any>} products
   * @param {any} filter
   * @returns {Array<any>}
   */
  static _tagProductsForFilter(products, filter) {
    if (!Array.isArray(products) || !filter) return products || [];
    const sectionId = String(filter.selectedsectionId ?? '').trim();
    const sectionGid = String(filter.selectedsectionGid ?? '').trim();
    const sectionLabel = String(filter.selectedsection ?? '').trim();
    if (!sectionId && !sectionGid && !sectionLabel) return products;

    return products.map(product => {
      if (!product) return product;
      /** @type {any} */
      const tagged = { ...product };
      tagged._cmrpFetchSectionId = sectionId;
      tagged._cmrpFetchSectionGid = sectionGid;
      tagged._cmrpFetchSectionLabel = sectionLabel;

      /** @type {Set<string>} */
      const idSet = new Set();
      const existing = tagged.sectionId;
      const existingArr = Array.isArray(existing)
        ? existing
        : existing != null && existing !== ''
          ? [existing]
          : [];
      existingArr.forEach(id => {
        const s = String(id).trim();
        if (s) idSet.add(s);
      });
      if (sectionId) idSet.add(sectionId);
      if (idSet.size) {
        tagged.sectionId = [...idSet];
      }

      return tagged;
    });
  }

  /**
   * @param {Array<any>} products
   * @param {any} filter
   * @returns {Array<any>}
   */
  _prepareFetchedProducts(products, filter) {
    const live = (products || []).filter(
      product => product && product.status !== 'DRAFT'
    );
    return ProductDisplayManager._tagProductsForFilter(live, filter);
  }

  /**
   * Align CMRP filter strings with DDCS metaobject text (entities, URL-escaped ampersands, spacing).
   * @param {unknown} value
   * @returns {string}
   */
  static _normalizeCmrpCompareKey(value) {
    return String(value ?? '')
      .replace(/%26/gi, '&')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#0*39;/gi, "'")
      .replace(/&#x0*27;/gi, "'")
      .replace(/&apos;/gi, "'")
      .toLowerCase()
      .replace(/\s+/g, '');
  }

  /** Enable in console: window.PDM_PERF_DEBUG = true */
  static _perfEnabled() {
    return typeof window !== 'undefined' && window.PDM_PERF_DEBUG === true;
  }

  /** @param {string} label */
  static _perfStart(label) {
    if (!ProductDisplayManager._perfEnabled()) return null;
    return { label, t0: performance.now() };
  }

  /**
   * @param {{ label: string, t0: number } | null} token
   * @param {Record<string, unknown>} [extra]
   */
  static _perfEnd(token, extra) {
    if (!token) return;
    const ms = performance.now() - token.t0;
    console.log(
      `[PDM perf] ${token.label}: ${ms.toFixed(1)}ms`,
      extra ?? ''
    );
  }

  /** @type {number | null} */
  static _ddcsCacheVersion = null;
  /** @type {Map<string, any> | null} */
  static _sectionsById = null;
  /** @type {Map<string, number> | null} */
  static _departmentSectionCounts = null;

  static _ensureDdcsIndexes() {
    const sections = window.courseData?.ddcs_section;
    const version = Array.isArray(sections) ? sections.length : 0;
    if (
      ProductDisplayManager._ddcsCacheVersion === version &&
      ProductDisplayManager._sectionsById
    ) {
      return;
    }
    ProductDisplayManager._ddcsCacheVersion = version;
    ProductDisplayManager._sectionsById = new Map();
    ProductDisplayManager._departmentSectionCounts = new Map();

    const normalize = v => ProductDisplayManager._normalizeCmrpCompareKey(v);
    const coursesById = new Map();
    for (const course of window.courseData?.ddcs_course || []) {
      coursesById.set(normalize(course.course_id), course);
    }
    const deptsById = new Map();
    for (const dept of window.courseData?.ddcs_campus_department || []) {
      deptsById.set(normalize(dept.campus_department_id), dept);
    }

    const buildStart = ProductDisplayManager._perfStart('ddcs-index-build');
    for (const section of sections || []) {
      if (section.section_id != null && section.section_id !== '') {
        ProductDisplayManager._sectionsById.set(
          String(section.section_id),
          section
        );
      }
      const course = coursesById.get(normalize(section.course_id));
      const dept = course
        ? deptsById.get(normalize(course.campus_department_id))
        : null;
      const deptName = normalize(dept?.name || '');
      if (deptName) {
        ProductDisplayManager._departmentSectionCounts.set(
          deptName,
          (ProductDisplayManager._departmentSectionCounts.get(deptName) || 0) +
            1
        );
      }
    }
    ProductDisplayManager._perfEnd(buildStart, {
      sections: version,
      courses: window.courseData?.ddcs_course?.length ?? 0,
      departments: window.courseData?.ddcs_campus_department?.length ?? 0,
    });
  }

  /**
   * O(1) lookup — avoids scanning all ddcs_section on every render.
   * @param {string} departmentName
   */
  static getDepartmentSectionCount(departmentName) {
    ProductDisplayManager._ensureDdcsIndexes();
    return (
      ProductDisplayManager._departmentSectionCounts?.get(
        ProductDisplayManager._normalizeCmrpCompareKey(departmentName)
      ) || 0
    );
  }

  /** @type {Promise<boolean> | null} */
  static _digitalDeliveryNoticeVisiblePromise = null;

  /** @returns {Promise<boolean>} */
  static getDigitalDeliveryNoticeVisible() {
    if (!ProductDisplayManager._digitalDeliveryNoticeVisiblePromise) {
      ProductDisplayManager._digitalDeliveryNoticeVisiblePromise =
        typeof window.isDigitalDeliveryNoticeStoreEnabled === 'function'
          ? window.isDigitalDeliveryNoticeStoreEnabled()
          : Promise.resolve(false);
    }
    return ProductDisplayManager._digitalDeliveryNoticeVisiblePromise;
  }

  /**
   * Collapse duplicate course lines (same campus/term/dept/course/section after normalize).
   * Keeps first occurrence order; prefers the entry with section GID / adoption filled in.
   * @param {Array<any>} filters
   * @returns {Array<any>}
   */
  dedupeSelectedFilters(filters) {
    if (!Array.isArray(filters) || filters.length < 2) {
      return filters || [];
    }
    const normalize = v => ProductDisplayManager._normalizeCmrpCompareKey(v);
    const keyOf = f =>
      [
        normalize(f.selectedCampus),
        normalize(f.selectedTerm),
        normalize(f.selectedDivision),
        normalize(f.selectedDepartment),
        normalize(f.selectedcourse),
        normalize(f.selectedsection),
      ].join('\u0001');
    const score = x =>
      (x.selectedsectionGid ? 2 : 0) +
      (x.selectedsectionId ? 1 : 0) +
      (x.adoption_status ? 1 : 0);
    /** @type {Map<string, any>} */
    const map = new Map();
    const order = [];
    for (const f of filters) {
      if (!f) continue;
      const k = keyOf(f);
      if (!map.has(k)) {
        map.set(k, f);
        order.push(k);
        continue;
      }
      const prev = map.get(k);
      if (score(f) > score(prev)) {
        map.set(k, f);
      }
    }
    return order.map(k => map.get(k));
  }

  /**
   * Main function to refresh and display products based on selected filters
   * @param {Array<any>} selectedFilters - Current selected filters
   */
  
  async refreshProductsDisplay(selectedFilters) {
    const generation = ++this._refreshGeneration;
    const perfTotal = ProductDisplayManager._perfStart('refreshProductsDisplay (total)');
    const perfStats = {
      filters: 0,
      sectionScans: 0,
      courseScans: 0,
      deptScans: 0,
      fetchCalls: 0,
      productsFetched: 0,
    };

    // Defensive programming: ensure selectedFilters is defined and is an array
    if (!selectedFilters || !Array.isArray(selectedFilters)) {
      console.warn(
        '⚠️ ProductDisplayManager: selectedFilters is not a valid array:',
        selectedFilters
      );
      selectedFilters = [];
    }

    const productsContainer = this.getProductsContainer();
    if (!productsContainer) return;

    if (selectedFilters.length > 0) {
      const deduped = this.dedupeSelectedFilters(selectedFilters);
      if (deduped.length !== selectedFilters.length) {
        selectedFilters.splice(0, selectedFilters.length, ...deduped);
      }
    }

    // Remove prior course sections and department titles immediately when starting a
    // non-empty refresh (avoids stale list/chips while fetches run or if an older refresh wins).
    if (selectedFilters.length > 0) {
      productsContainer.innerHTML = '';
    }

    if (selectedFilters.length === 0) {
      if (generation !== this._refreshGeneration) {
        return;
      }
      this.showEmptyState();
      productsContainer.classList.remove('course-layout');
      productsContainer.innerHTML = '';
      await this.handleEmptyFiltersState();
      this.hideLoadingState();
      await window.generateDepartmentChips?.();
      if (generation !== this._refreshGeneration) {
        return;
      }
      if (
        window.courseMaterialsSidebar &&
        typeof window.courseMaterialsSidebar.syncBubblesFromFilters === 'function'
      ) {
        window.courseMaterialsSidebar.syncBubblesFromFilters([]);
      }
      document.dispatchEvent(
        new CustomEvent('product-display:updated', { bubbles: true })
      );
      return;
    }

    if (generation !== this._refreshGeneration) {
      return;
    }
    
    // One clear before parallel fetches; do not reset inside each task (would race and duplicate rows).
    // @ts-ignore
    window.courseData.products_Materials = [];

    perfStats.filters = selectedFilters.length;
    if (ProductDisplayManager._perfEnabled()) {
      console.log('[PDM perf] refreshProductsDisplay start', {
        filters: selectedFilters.length,
        ddcs_section: window.courseData?.ddcs_section?.length ?? 0,
        ddcs_course: window.courseData?.ddcs_course?.length ?? 0,
        generation,
      });
    }

    const perfFetch = ProductDisplayManager._perfStart('parallel filter resolve + fetchProducts');
    const promises = selectedFilters.map(async (item, filterIndex) => {
      const perfFilter = ProductDisplayManager._perfStart(
        `filter[${filterIndex}] resolve`
      );
      const normalize = v => ProductDisplayManager._normalizeCmrpCompareKey(v);

      // Prefer campus on each filter (student-id + dual CMRP) so we never match DDCS
      // with sidebar.campus_entity_id from the other form or a stale value.
      const campusMatch = (window.courseData.ddcs_campus_entity || []).find(
        campus => normalize(campus.name) === normalize(item.selectedCampus),
      );
      let campusId =
        campusMatch?.campus_entity_id ||
        window.courseMaterialsSidebar?.campus_entity_id;
      if (!campusId) {
        ProductDisplayManager._perfEnd(perfFilter, { result: 'no campusId' });
        return [];
      }
      if (!item.hasOwnProperty('selectedsectionGid')||!item.hasOwnProperty('adoption_status')) {
          // @ts-ignore
          let filteredDepartments;
          let filteredDivision;
          
          if (item.selectedDivision != '') {
            //const queryDataDivision = "fields.campus_entity_id:'"+campusEntityId+"' fields.name:'"+item.selectedDivision+"' fields.disabled_flag:false";
            
            filteredDivision =
              window.courseData.ddcs_campus_division.filter(
                division =>
                  normalize(division.campus_entity_id) === normalize(campusId) &&
                  normalize(division.name) === normalize(item.selectedDivision)
              );
              if(filteredDivision.length == 0){
                filteredDepartments =
                  window.courseData.ddcs_campus_department.filter(
                    department =>
                      department.campus_entity_id === campusId &&
                      (department.name === item.selectedDepartment ||
                        String(department.campus_department_id) === String(item.selectedDepartment))
                  );
              }else{
                for (var div = 0; div < filteredDivision.length; div++) {
                  filteredDepartments =
                    window.courseData.ddcs_campus_department.filter(
                      department =>
                        department.campus_entity_id === campusId &&
                        department.campus_division_id ===
                          filteredDivision[div].campus_division_id &&
                        (department.name === item.selectedDepartment ||
                          String(department.campus_department_id) === String(item.selectedDepartment))
                    );
                if (filteredDepartments.length > 0) {
                  break;
                }
              }
            }
          } else {
            filteredDepartments =
              window.courseData.ddcs_campus_department.filter(
                department =>
                  normalize(department.campus_entity_id) === normalize(campusId) &&
                  (normalize(department.name) === normalize(item.selectedDepartment) ||
                    normalize(department.campus_department_id) === normalize(item.selectedDepartment) ||
                    normalize(department.department_id || '') === normalize(item.selectedDepartment))
              );
          }

          if (!filteredDepartments || filteredDepartments.length === 0) {
            ProductDisplayManager._perfEnd(perfFilter, { result: 'no department' });
            return [];
          }
          const filteredDepartment = filteredDepartments[0];
          item.selectedDepartment = filteredDepartment.name;

          const courseArr = window.courseData.ddcs_course;
          perfStats.courseScans += courseArr.length;
          const filteredCourse = courseArr.filter(
            course =>
              normalize(course.campus_entity_id) === normalize(campusId) &&
              normalize(course.campus_department_id) === normalize(filteredDepartment.campus_department_id) &&
              normalize(course.coursenumber) === normalize(item.selectedcourse)
          );
          const sectionArr = window.courseData.ddcs_section;
          perfStats.sectionScans += sectionArr.length;
          const filteredSection = sectionArr.filter(
            section =>
              normalize(section.campus_entity_id) === normalize(campusId) &&
              filteredCourse[0] &&
              normalize(section.course_id) === normalize(filteredCourse[0].course_id) &&
              (normalize(section.name) === normalize(item.selectedsection || '') ||
                normalize(section.section_id || '') === normalize(item.selectedsection || ''))
          );

          if (!filteredCourse.length || !filteredSection.length) {
            ProductDisplayManager._perfEnd(perfFilter, { result: 'no match' });
            return [];
          }
          item.selectedsectionGid = filteredSection[0]?.gid || '';
          item.selectedsectionId = filteredSection[0]?.section_id || '';
          item.adoption_status = filteredSection[0]?.adoption_status || ''; 

          // @ts-ignore
          const sectionGid = filteredSection[0]?.gid || '';
          const queryDataProd = "metafields.ddcs.ddcs_section:'"+sectionGid+"'";  
          
          const newProducts = await this.fetchProducts(queryDataProd);
          perfStats.fetchCalls += 1;
          const taggedProducts = this._prepareFetchedProducts(newProducts, item);
          perfStats.productsFetched += taggedProducts.length;
          ProductDisplayManager._perfEnd(perfFilter, {
            path: 'resolve+fetch',
            products: taggedProducts.length,
            sectionGid,
          });
          return taggedProducts;
      }else{
          if(item.hasOwnProperty('selectedsectionGid')){
            const queryDataProd = "metafields.ddcs.ddcs_section:'"+item.selectedsectionGid+"'";  
          
            const newProducts = await this.fetchProducts(queryDataProd);
            perfStats.fetchCalls += 1;
            const taggedProducts = this._prepareFetchedProducts(newProducts, item);
            perfStats.productsFetched += taggedProducts.length;
            ProductDisplayManager._perfEnd(perfFilter, {
              path: 'gid-only fetch',
              products: taggedProducts.length,
            });
            return taggedProducts;
          }else{
            
            let filteredDepartments;
            let filteredDivision;
            if(item.selectedDivision != ''){
            //const queryDataDivision = "fields.campus_entity_id:'"+campusEntityId+"' fields.name:'"+item.selectedDivision+"' fields.disabled_flag:false";
              filteredDivision =
                window.courseData.ddcs_campus_division.filter(
                  division =>
                    normalize(division.campus_entity_id) === normalize(campusId) &&
                    normalize(division.name) === normalize(item.selectedDivision)
                );
              if(filteredDivision.length == 0){
                filteredDepartments =
                  window.courseData.ddcs_campus_department.filter(
                    department =>
                      normalize(department.campus_entity_id) === normalize(campusId) &&
                      (normalize(department.name) === normalize(item.selectedDepartment) ||
                        normalize(department.campus_department_id) === normalize(item.selectedDepartment))
                  );
              }else{
                for (var div = 0; div < filteredDivision.length; div++) {
                  filteredDepartments =
                    window.courseData.ddcs_campus_department.filter(
                      department =>
                        normalize(department.campus_entity_id) === normalize(campusId) &&
                        normalize(department.campus_division_id) === normalize(filteredDivision[div].campus_division_id) &&
                        (normalize(department.name) === normalize(item.selectedDepartment) ||
                          normalize(department.campus_department_id) === normalize(item.selectedDepartment))
                    );
                if (filteredDepartments.length > 0) {
                  break;
                }
              }
            }
          } else {
            filteredDepartments =
              window.courseData.ddcs_campus_department.filter(
                department =>
                  normalize(department.campus_entity_id) === normalize(campusId) &&
                  (normalize(department.name) === normalize(item.selectedDepartment) ||
                    normalize(department.campus_department_id) === normalize(item.selectedDepartment))
              );
            }
            
            if (!filteredDepartments || filteredDepartments.length === 0) {
              ProductDisplayManager._perfEnd(perfFilter, { result: 'no department (else)' });
              return [];
            }
            
            const filteredDepartment = filteredDepartments[0];
            item.selectedDepartment = filteredDepartment.name;

            const filteredCourse = window.courseData.ddcs_course.filter(
              course =>
                normalize(course.campus_entity_id) === normalize(campusId) &&
                normalize(course.campus_department_id) === normalize(filteredDepartment.campus_department_id) &&
                normalize(course.coursenumber) === normalize(item.selectedcourse)
            );
            perfStats.courseScans += window.courseData.ddcs_course.length;
            
            const filteredSection = window.courseData.ddcs_section.filter(
              section =>
                normalize(section.campus_entity_id) === normalize(campusId) &&
                filteredCourse[0] &&
                normalize(section.course_id) === normalize(filteredCourse[0].course_id) &&
                (normalize(section.name) === normalize(item.selectedsection || '') ||
                  normalize(section.section_id || '') === normalize(item.selectedsection || ''))
            );
            perfStats.sectionScans += window.courseData.ddcs_section.length;
            
            if (!filteredCourse.length || !filteredSection.length) {
              ProductDisplayManager._perfEnd(perfFilter, { result: 'no match (else branch)' });
              return [];
            }
            item.selectedsectionGid = filteredSection[0]?.gid || '';
            item.selectedsectionId = filteredSection[0]?.section_id || '';
            item.adoption_status = filteredSection[0]?.adoption_status || ''; 

            // @ts-ignore
            const queryDataProd = "metafields.ddcs.ddcs_section:'"+filteredSection[0].gid+"'";  
            
            const newProducts = await this.fetchProducts(queryDataProd);
            perfStats.fetchCalls += 1;
            const taggedProducts = this._prepareFetchedProducts(newProducts, item);
            perfStats.productsFetched += taggedProducts.length;
            ProductDisplayManager._perfEnd(perfFilter, {
              path: 'else resolve+fetch',
              products: taggedProducts.length,
            });
            return taggedProducts;
          }

          return [];
      }
    });


    const productBatches = await Promise.all(promises);
    // @ts-ignore
    window.courseData.products_Materials = productBatches.flat();
    ProductDisplayManager._perfEnd(perfFetch, {
      fetchCalls: perfStats.fetchCalls,
      productsFetched: perfStats.productsFetched,
      sectionScans: perfStats.sectionScans,
      courseScans: perfStats.courseScans,
      products_Materials:
        window.courseData?.products_Materials?.length ?? 0,
    });
    // @ts-ignore

    if (generation !== this._refreshGeneration) {
      return;
    }
    
    // Check if any of the selected filters have adoption_status
    const perfAdoption = ProductDisplayManager._perfStart('adoption split');
    const adoptionCourses = await this.getAdoptionCourses(selectedFilters);
    const coursesWithAdoption = adoptionCourses;
    const coursesWithoutAdoption = await this.getCoursesWithoutAdoption(selectedFilters);
    ProductDisplayManager._perfEnd(perfAdoption, {
      withAdoption: coursesWithAdoption.length,
      withoutAdoption: coursesWithoutAdoption.length,
    });

    const perfRender = ProductDisplayManager._perfStart('render DOM');
    productsContainer.innerHTML = '';
    //console.log("this.components.selectionManager.selectedProducts ==> ", this.components.selectionManager.selectedProducts)
    if (coursesWithAdoption.length > 0 || coursesWithoutAdoption.length > 0) {
      this.hideEmptyState();
      productsContainer.classList.add('course-layout');

      

      // Then display normal product sections
      if (coursesWithoutAdoption.length > 0) {
        await this.displayNormalProductSections(coursesWithoutAdoption);
      }

      // Display adoption status cards first
      if (coursesWithAdoption.length > 0) {
        this.displayAdoptionStatusCards(coursesWithAdoption);
      }
    } else {
      this.hideEmptyState();

      // Process products by department
      const productsByDepartment =
        this.processProductsByDepartment(selectedFilters);

      if (productsContainer) {
        productsContainer.innerHTML = '';
        productsContainer.classList.add('course-layout');
      }

      const departments = Object.keys(productsByDepartment);
      for (const department of departments) {
          await this.displayDepartmentSection(department, productsByDepartment[department]);
      }
    }
    ProductDisplayManager._perfEnd(perfRender);

    if (generation !== this._refreshGeneration) {
      return;
    }
    
    const perfChips = ProductDisplayManager._perfStart('generateDepartmentChips');
    await window.generateDepartmentChips?.();
    ProductDisplayManager._perfEnd(perfChips);
    
    if (generation !== this._refreshGeneration) {
      return;
    }
    
    // Hide loading state only after department chips are generated and displayed
    this.hideLoadingState();
    
    // Sync course bubbles in sidebar with the filters that were just rendered
    if (window.courseMaterialsSidebar && typeof window.courseMaterialsSidebar.syncBubblesFromFilters === 'function') {
      window.courseMaterialsSidebar.syncBubblesFromFilters(selectedFilters);
    }
    
    document.dispatchEvent(new CustomEvent('product-display:updated', {
      bubbles: true
    }));

    ProductDisplayManager._perfEnd(perfTotal, {
      filters: perfStats.filters,
      sectionScans: perfStats.sectionScans,
      courseScans: perfStats.courseScans,
      fetchCalls: perfStats.fetchCalls,
      productsFetched: perfStats.productsFetched,
    });
  }

  /**
   * Get courses with adoption status
   * @param {Array<any>} selectedFilters - Selected filters
   * @returns {Array<any>} Courses with adoption status
   * @private
   */
  async getAdoptionCourses(selectedFilters) {
    /** @type {Array<any>} */
    const adoptionCourses = [];
    for (const filter of selectedFilters) {
      if (
          filter &&
          filter.adoption_status &&
          filter.adoption_status !== '' &&
          (filter.adoption_status !== '500' ||
            !(window.courseData?.products_Materials || []).some(
              p =>
                p &&
                (!filter.selectedsection ||
                  (p.section || []).includes(filter.selectedsection))
            ))
      ) {
          let messageCode = 'No books required for this course.';
          switch (filter.adoption_status) {
            case '100': 
              messageCode = 'Course Materials have not been determined. Check back soon for updates.';
              break;
            case '200': 
            case '300':
            case '499':
            case '500':
              messageCode = 'Course Materials are being finalized. Check back soon for updates.';
              break;
            case '350': 
              messageCode = 'Free "Open Educational Resources" are required for this course. Please see your Instructor.';
              break;
            case '400':
              messageCode = ProductDisplayManager.getCmrpSamHiddenMessage();
              break;
            case '450': 
            case '460':
              messageCode = 'No bookstore supplied material. See your instructor for any custom material.';
              break;
            case '600': 
              messageCode = 'This class is cancelled. Please contact your campus with any questions.';
              break;
          
            default:
              break;
          }
          adoptionCourses.push({
              filter: filter,
              section: filter,
              courseCode: `${filter.selectedDepartment} ${filter.selectedcourse} Section ${filter.selectedsection}`,
              note: messageCode || 'No books required for this course.',
          });
      }
    }

    return adoptionCourses;
  }

  /**
   * Get courses without adoption status
   * @param {Array<any>} selectedFilters - Selected filters
   * @returns {Array<any>} Courses without adoption status
   * @private
   */
  async getCoursesWithoutAdoption(selectedFilters) {
    const adoptionWithoutCourses = [];
    for (const filter of selectedFilters) {
      if (
        !filter ||
        !filter.adoption_status ||
        filter.adoption_status === '' ||
        (filter.adoption_status === '500' &&
          (window.courseData?.products_Materials || []).some(
            p =>
              p &&
              (!filter.selectedsection ||
                (p.section || []).includes(filter.selectedsection))
          ))
      ){
        adoptionWithoutCourses.push(filter);
      }
    }

    return adoptionWithoutCourses;
  }

  /**
   * Process products and group them by department
   * @param {Array<any>} selectedFilters - Selected filters
   * @returns {Record<string, any>} Products grouped by department
   * @private
   */
  processProductsByDepartment(selectedFilters) {
    const productsByDepartment = {};

    // @ts-ignore
    // @ts-ignore
    // @ts-ignore
    selectedFilters.forEach((filter, filterIndex) => {
      // @ts-ignore
      const filteredProducts = window.courseData.products_Materials.filter(
        product =>
          product &&
          ProductDisplayManager._productRowMatchesCourseSectionIds(
            product,
            filter
          )
      );

      // @ts-ignore
      filteredProducts.forEach(product => {
        const productsByDept = productsByDepartment;
        
        // @ts-ignore
        if (!productsByDept[filter.selectedDepartment]) {
          // @ts-ignore
          productsByDept[filter.selectedDepartment] = {
            products: [],
            courseInfo: {
              campus: filter.selectedCampus,
              term: filter.selectedTerm,
              division: filter.selectedDivision,
              course: filter.selectedcourse,
              section: filter.selectedsection,
              // @ts-ignore
              department: filter.selectedDepartment,
              termId: filter.selectedTermId,
              selectedsectionGid: filter.selectedsectionGid,
              selectedsectionId: filter.selectedsectionId
            },
          };
        }
        // @ts-ignore
        productsByDept[filter.selectedDepartment].products.push(product);
      });
    });

    return productsByDepartment;
  }

  /**
   * Build bundle-aware display list: each entry is { parentProduct, childProducts }.
   * Parent = product with course_materials.is_package_flag === true.
   * Children = products whose course_materials.title_id is in the parent's course_materials.package_items.
   * Child products are only shown under their parent (hidden from the main group).
   * @param {Array<any>} products - Flat list of products
   * @returns {Array<{ parentProduct: any, childProducts: any[] }>} Display list
   * @private
   */
  buildBundleDisplayList(products) {
    if (!Array.isArray(products) || products.length === 0) {
      return [];
    }

    const getCm = (p) => p?.metafields?.course_materials || p?.metafields?.course_materials?.value;
    const isPackageParent = (p) => {
      const cm = getCm(p);
      const v = cm?.is_package_flag ?? cm?.is_package_flag?.value;
      return v === true || String(v).toLowerCase() === 'true';
    };
    const getPackageItems = (p) => {
      const cm = getCm(p);
      const raw = cm?.package_items ?? cm?.package_items?.value;
      if (Array.isArray(raw)) return raw;
      if (raw == null) return [];
      if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          return Array.isArray(parsed) ? parsed : [];
        } catch (_) {
          return [];
        }
      }
      return [];
    };
    const getTitleId = (p) => {
      const cm = getCm(p);
      const v = cm?.title_id ?? cm?.title_id?.value;
      return v == null ? '' : String(v);
    };

    const parents = products.filter((p) => isPackageParent(p));
    /** @type {Array<{ parentProduct: any, childProducts: any[] }>} */
    const rows = [];

    parents.forEach((parent) => {
      const packageItems = getPackageItems(parent);
      const childProducts = products.filter((p) => {
        if (p === parent) return false;
        const tid = getTitleId(p);
        return tid !== '' && packageItems.some((item) => String(item) === tid);
      });
      rows.push({ parentProduct: parent, childProducts });
    });

    const seenAsChild = new Set();
    rows.forEach((r) => r.childProducts.forEach((c) => seenAsChild.add(c)));

    products.forEach((p) => {
      if (parents.includes(p) || seenAsChild.has(p)) return;
      rows.push({ parentProduct: p, childProducts: [] });
    });

    return rows;
  }

  /**
   * Check if product has rental options (RENT, RENT NEW, RENT USED)
   * @param {any} product - Product object
   * @returns {boolean} True if has rental options
   */
  hasRentalOptions(product) {
    if (!product.variants) return false;
    // @ts-ignore
    return product.variants.some(variant => {
      const option2 = variant.option2?.toLowerCase() || '';
      return option2.includes('rent');
    });
  }

  /**
   * Get the lowest price for a specific format
   * @param {any} product - Product object
   * @param {string} format - Format to filter by (Digital, Print)
   * @returns {string} Formatted price string
   */
  getLowestPriceForFormat(product, format) {
    if (!product.variants) return 'From $0.00';
    
    // @ts-ignore
    const formatVariants = product.variants.filter(variant => {
      const option1 = variant.option1?.toLowerCase() || '';
      return option1.includes(format.toLowerCase());
    });
    
    if (formatVariants.length === 0) return 'From $0.00';
    
    const prices = formatVariants
      // @ts-ignore
      .filter(variant => variant.available)
      // @ts-ignore
      .map(variant => parseFloat(variant.price))
      // @ts-ignore
      .filter(price => !isNaN(price));
    
    if (prices.length === 0) return 'From $0.00';
    
    const lowestPrice = Math.min(...prices);
    return `From $${lowestPrice.toFixed(2)}`;
  }

  /**
   * Display adoption status cards for courses
   * @param {Array<any>} adoptionCourses - Courses with adoption status
   */
  displayAdoptionStatusCards(adoptionCourses) {
    const productsContainer = this.getProductsContainer();
    if (!productsContainer) return;

    // Create individual sections for each course (not grouped by department)
    adoptionCourses.forEach(adoptionCourse => {
      const { filter, section, courseCode, note } = adoptionCourse;
      const divisionName = String(filter.selectedDivision || '').trim();
      const departmentName = String(filter.selectedDepartment || '').trim();

      // Create individual course section
      const courseSection = document.createElement('div');
      courseSection.className = 'course-section';
      courseSection.dataset.departmentFilter = departmentName.toLowerCase();
      if (divisionName) {
        courseSection.innerHTML = `
                  <div class="department-title">
                      <span class="department-name">${divisionName}</span>
                  </div>
              `;
      }

      // Add adoption card for this specific course
      const statusInfo = this.getAdoptionStatusInfo(note);
      const adoptionCard = document.createElement('div');
      adoptionCard.className = 'adoption-status-section';
      adoptionCard.innerHTML = this.createAdoptionCardHTML(
        filter,
        section,
        courseCode,
        note,
        statusInfo
      );

      courseSection.appendChild(adoptionCard);
      productsContainer.appendChild(courseSection);
    });
  }

  /**
   * Display normal product sections (without adoption status)
   * @param {Array<any>} coursesWithoutAdoption - Courses without adoption status
   */
  async displayNormalProductSections(coursesWithoutAdoption) {
    const productsByDepartment = {};
    /** @type {Map<string, Set<string>>} */
    const seenProductIdsByCourse = new Map();

    // @ts-ignore
    // @ts-ignore
    // @ts-ignore
    coursesWithoutAdoption.forEach((filter, index) => {
      // @ts-ignore
      const filteredProducts = window.courseData.products_Materials.filter(
        product =>
          product &&
          ProductDisplayManager._productRowMatchesCourseSectionIds(
            product,
            filter
          )
      );
      //console.log("filteredProducts ==> ", filteredProducts)
      // @ts-ignore
      filteredProducts.forEach(product => {
        const courseKey = [
          filter.selectedDivision || '',
          filter.selectedDepartment,
          filter.selectedcourse,
          filter.selectedsection,
        ].join('|');
        /** @type {Record<string, any>} */
        const productsByDept = productsByDepartment;
        if (!productsByDept[courseKey]) {
          productsByDept[courseKey] = {
            products: [],
            courseInfo: {
              campus: filter.selectedCampus,
              term: filter.selectedTerm,
              division: filter.selectedDivision,
              course: filter.selectedcourse,
              section: filter.selectedsection,
              department: filter.selectedDepartment,
              termId: filter.selectedTermId,
              selectedsectionGid: filter.selectedsectionGid,
              selectedsectionId: filter.selectedsectionId
            },
          };
        }
        const pid = ProductDisplayManager._productDedupeId(product);
        if (pid) {
          if (!seenProductIdsByCourse.has(courseKey)) {
            seenProductIdsByCourse.set(courseKey, new Set());
          }
          const seen = seenProductIdsByCourse.get(courseKey);
          if (seen.has(pid)) {
            return;
          }
          seen.add(pid);
        }
        productsByDept[courseKey].products.push(product);
      });
    });

    // Display sections with products
    const departments = Object.keys(productsByDepartment);
    // Use for...of para permitir o uso de await
    for (const department of departments) {
        /** @type {Record<string, any>} */
        const productsByDept = productsByDepartment;
        await this.displayDepartmentSection(department, productsByDept[department]);
    }

    // Display empty sections for courses without products
    for (const filter of coursesWithoutAdoption) {
        const courseKey = [
          filter.selectedDivision || '',
          filter.selectedDepartment,
          filter.selectedcourse,
          filter.selectedsection,
        ].join('|');
        /** @type {Record<string, any>} */
        const productsByDept = productsByDepartment;
        if (!productsByDept[courseKey]) {
          await this.displayDepartmentSection(courseKey, {
              products: [],
              courseInfo: {
                  campus: filter.selectedCampus,
                  term: filter.selectedTerm,
                  division: filter.selectedDivision,
                  course: filter.selectedcourse,
                  section: filter.selectedsection,
                  department: filter.selectedDepartment,
                  termId: filter.selectedTermId,
                  selectedsectionGid: filter.selectedsectionGid,
                  selectedsectionId: filter.selectedsectionId
              },
          });
        }
    }
  }

  /**
   * Display a department section with its products
   * @param {string} department - Department key
   * @param {any} sectionData - Section data with products and course info
   */
  async displayDepartmentSection(department, sectionData) {
    const perfDept = ProductDisplayManager._perfStart(
      `displayDepartmentSection: ${department}`
    );
    const productsContainer = this.getProductsContainer();
    if (!productsContainer) {
      ProductDisplayManager._perfEnd(perfDept, { result: 'no container' });
      return;
    }
    ProductDisplayManager._ensureDdcsIndexes();
    // Find the section data to get instructor information (normalize IDs to string for comparison)
    const selectedsectionIdRaw = sectionData.courseInfo.selectedsectionId;
    const sectionIdKey = String(selectedsectionIdRaw ?? '');
    let sectionInfo = ProductDisplayManager._sectionsById?.get(sectionIdKey);
    if (!sectionInfo && sectionIdKey && window.courseData?.ddcs_section?.length) {
      sectionInfo = window.courseData.ddcs_section.find(
        s => String(s?.section_id ?? s?.section_d ?? '') === sectionIdKey
      );
    }
    // Get instructor name from first and last name fields
    const instructorName = sectionInfo
      ? `${sectionInfo.instructor_first_name || ''} ${
          sectionInfo.instructor_last_name || ''
        }`.trim() || 'TBA'
      : 'TBA';

    // Get section display name or fall back to section name
    const sectionNumber =
      sectionInfo?.display_name || sectionData.courseInfo.section;

    const sectionId = String(selectedsectionIdRaw ?? '');
    const courseId = sectionInfo?.course_id != null ? String(sectionInfo.course_id) : '';

    /** Get .note from metaobject; try note/notes and accept string or value object */
    const getNote = (obj) => {
      if (!obj || typeof obj !== 'object') return null;
      const raw = obj.note ?? obj.notes ?? obj.Note ?? obj.Notes ?? obj.fields?.note ?? obj.fields?.notes;
      if (raw == null) return null;
      const str = typeof raw === 'string' ? raw : (raw?.value ?? String(raw));
      return typeof str === 'string' && str.trim() !== '' ? str.trim() : null;
    };

    /** Match section_d value (handles nested .value and .fields) */
    const sectionDVal = (c) => {
      const v = c?.section_d ?? c?.fields?.section_d ?? c?.section_id ?? c?.fields?.section_id;
      return v != null ? String(typeof v === 'object' && v.value !== undefined ? v.value : v).trim() : '';
    };

    /** Match course_id value (handles nested .value and .fields) */
    const courseIdVal = (c) => {
      const v = c?.course_id ?? c?.fields?.course_id;
      return v != null ? String(typeof v === 'object' && v.value !== undefined ? v.value : v).trim() : '';
    };

    let sectionNote = null;
    let courseNote = null;
    let ddcsCourseFromProxy = [];

    try {
      const fromPage = window.courseData?.ddcs_course;
      if (Array.isArray(fromPage) && fromPage.length > 0) {
        ddcsCourseFromProxy = fromPage;
      } else {
        const query = sectionId ? `fields.section_d:'${sectionId}'` : '';
        const promiseKey = sectionId ? `ddcs_course_${sectionId}` : 'ddcs_course';
        if (!this._ddcsCourseProxyPromises) this._ddcsCourseProxyPromises = {};
        if (!this._ddcsCourseProxyPromises[promiseKey]) {
          this._ddcsCourseProxyPromises[promiseKey] = this.fetchMetaobjects('ddcs_course', query);
        }
        ddcsCourseFromProxy = await this._ddcsCourseProxyPromises[promiseKey];
      }
      if (Array.isArray(ddcsCourseFromProxy)) {
        let sectionMeta = ddcsCourseFromProxy.find(c => sectionDVal(c) === sectionId);
        if (sectionMeta == null && sectionId && window.courseData?.ddcs_section?.length) {
          const fromSection = window.courseData.ddcs_section.find(
            s => sectionDVal(s) === sectionId || String(s?.section_id ?? s?.section_d ?? '') === sectionId
          );
          if (fromSection) sectionMeta = fromSection;
        }
        sectionNote = getNote(sectionMeta);
        const courseMeta = ddcsCourseFromProxy.find(c => courseIdVal(c) === courseId);
        courseNote = getNote(courseMeta);
      }
    } catch {
      const ddcsCourse = window.courseData?.ddcs_course ?? [];
      const ddcsSection = window.courseData?.ddcs_section ?? [];
      const sectionMeta = sectionInfo
        ?? ddcsCourse.find(c => sectionDVal(c) === sectionId)
        ?? ddcsSection.find(
          s => sectionDVal(s) === sectionId || String(s?.section_id ?? s?.section_d ?? '') === sectionId
        );
      sectionNote = getNote(sectionMeta);
      const courseMeta = ddcsCourse.find(c => courseIdVal(c) === courseId);
      courseNote = getNote(courseMeta);
    }

    const section = document.createElement('div');
    section.className = 'course-section';

    const departmentTitle = document.createElement('div');
    departmentTitle.className = 'department-title';

    // Extract department name from courseKey or use sectionData
    const departmentName = sectionData.courseInfo
      ? sectionData.courseInfo.department
      : department.split('-')[0];
    const divisionName = String(sectionData.courseInfo?.division || '').trim();
    section.dataset.departmentFilter = String(departmentName || '')
      .toLowerCase()
      .trim();

    // Count sections for this department (indexed — O(1) vs scanning 15k+ sections per card)
    const departmentSectionsCount =
      ProductDisplayManager.getDepartmentSectionCount(departmentName);

    const samSet = ProductDisplayManager.getCmrpSamSkuSet();
    if (samSet.size > 0) {
      const orig = sectionData.products;
      const allSam =
        Array.isArray(orig) &&
        orig.length > 0 &&
        orig.every(p => ProductDisplayManager.productHasSamSku(p, samSet));
      const mixed =
        Array.isArray(orig) &&
        orig.some(p => ProductDisplayManager.productHasSamSku(p, samSet)) &&
        !allSam;
      if (allSam) {
        sectionData.products = [];
        sectionData._cmrpSamOnlyAllSam = true;
      } else if (mixed) {
        sectionData.products = orig.filter(
          p => !ProductDisplayManager.productHasSamSku(p, samSet)
        );
        sectionData._cmrpSamOnlyAllSam = false;
      } else {
        sectionData._cmrpSamOnlyAllSam = false;
      }
    } else {
      sectionData._cmrpSamOnlyAllSam = false;
    }

    const sectionIdNeedle = String(
      sectionData.courseInfo.selectedsectionId ?? ''
    ).trim();
    const filteredItems = sectionData.products.filter(item =>
      ProductDisplayManager._productRowMatchesCourseSectionIds(
        item,
        sectionData.courseInfo
      )
    );
    const filteredProducts = filteredItems.reduce((acc, product) => {
      
      if (product.metafields.book_type_ind === "CEB") {
        const printRelationshipISBN = product.metafields.print_relationship_isbn;
        if (printRelationshipISBN && printRelationshipISBN !== "") {
          const isbnArray = JSON.parse(printRelationshipISBN);
          const shouldRemove = isbnArray.some(isbn => {
              return filteredItems.some(item => 
                  item.metafields.isbn_ean === isbn &&
                  item.material_type === product.material_type 
              );
          });
          if (shouldRemove) {
            const variantsToAdd = product.variants || [];
            const parentProduct = filteredItems.find(item => 
                item.metafields.book_type_ind === "TXT" && 
                item.material_type === product.material_type && 
                isbnArray.some(isbn => item.metafields.isbn_ean === isbn) 
            );
            if (parentProduct) {
              parentProduct.variants = [...(parentProduct.variants || []), ...variantsToAdd];
            }
            return acc; 
          }
        }
      }
      acc.push(product);
      return acc;
    }, []);
    if (divisionName) {
      departmentTitle.innerHTML = `
              <span class="department-name">${divisionName}</span>
          `;
      section.appendChild(departmentTitle);
    }

    const courseCard = document.createElement('div');
    courseCard.className = 'course-card';
    // Expose course context on the card so add-to-cart can read it (checkbox has no course data).
    courseCard.dataset.campus = sectionData.courseInfo.campus ?? '';
    courseCard.dataset.term = sectionData.courseInfo.term ?? '';
    courseCard.dataset.division = sectionData.courseInfo.division ?? '';
    courseCard.dataset.department = sectionData.courseInfo.department ?? '';
    courseCard.dataset.course = sectionData.courseInfo.course ?? '';
    courseCard.dataset.section = sectionData.courseInfo.section ?? '';
    courseCard.dataset.termId = sectionData.courseInfo.termId ?? '';

    const headerSection = document.createElement('div');
    headerSection.className = 'course-header';
    // Render using course header template (mimics snippets/course-header.liquid)
    
    let requiredCount =
    filteredProducts.length > 0
        ? filteredProducts.filter(
            /** @param {any} p */ p => 
                ['Required'].includes(p.material_type)
          ).length
        : 0;
    const choiceMaterials = filteredProducts.filter(
      /** @param {any} p */ p => 
      ['Choice', 'Choice/Required', 'Choice/Recommended'].includes(p.material_type)
    );
    let choice_minimum = '0'
    if (choiceMaterials.length > 0) {
        // @ts-ignore
        let filteredDepartments;
        let filteredDivision;
        const normalize = v => ProductDisplayManager._normalizeCmrpCompareKey(v);
        const campusMatchChoice = (window.courseData.ddcs_campus_entity || []).find(
          campus => normalize(campus.name) === normalize(sectionData.courseInfo.campus),
        );
        let campusId =
          campusMatchChoice?.campus_entity_id ||
          window.courseMaterialsSidebar?.campus_entity_id;
        if(sectionData.courseInfo?.division && sectionData.courseInfo.division != ''){
          filteredDivision = window.courseData.ddcs_campus_division.filter(division => normalize(division.campus_entity_id) === normalize(campusId) && normalize(division.name) === normalize(sectionData.courseInfo.division));
            for(var div=0;div<filteredDivision.length;div++){
              filteredDepartments = window.courseData.ddcs_campus_department.filter(department => normalize(department.campus_entity_id) === normalize(campusId) && normalize(department.campus_division_id) === normalize(filteredDivision[div].campus_division_id) && normalize(department.name) === normalize(sectionData.courseInfo.department));
              if(filteredDepartments.length > 0){
                break;
              }
            }
        }else{
          filteredDepartments = window.courseData.ddcs_campus_department.filter(department => normalize(department.campus_entity_id) === normalize(campusId) && normalize(department.name) === normalize(sectionData.courseInfo.department));
        }
        if (!filteredDepartments || filteredDepartments.length === 0) {
          filteredDepartments = window.courseData.ddcs_campus_department.filter(department => normalize(department.campus_entity_id) === normalize(campusId) && normalize(department.name) === normalize(sectionData.courseInfo.department));
        }
        if (!filteredDepartments || filteredDepartments.length === 0) {
          return;
        }
        const filteredCourse = window.courseData.ddcs_course.filter(course => normalize(course.campus_entity_id) === normalize(campusId) && normalize(course.campus_department_id) === normalize(filteredDepartments[0].campus_department_id) && normalize(course.coursenumber) === normalize(sectionData.courseInfo.course));
        const filteredSection = window.courseData.ddcs_section.filter(section => normalize(section.campus_entity_id) === normalize(campusId) && normalize(section.course_id) === normalize(filteredCourse[0].course_id) && normalize(section.name) === normalize(sectionData.courseInfo.section)); 
        choice_minimum = filteredSection[0].choice_minimum
        requiredCount = requiredCount + parseInt(filteredSection[0].choice_minimum);
    }

    const NOTE_NA_VALUES = ['N/A', 'NA', 'na', 'n/a'];
    const DESKTOP_TRUNCATE = 400;
    const MOBILE_TRUNCATE = 100;
    const MAX_NOTE_CHARS = 4000;

    const isValidNote = (note) => {
      if (note == null || typeof note !== 'string') return false;
      const t = note.trim();
      return t.length > 0 && !NOTE_NA_VALUES.includes(t);
    };

    const truncateAtBlank = (text, maxLen) => {
      if (!text || text.length <= maxLen) return text;
      const slice = text.slice(0, maxLen);
      const lastSpace = slice.lastIndexOf(' ');
      if (lastSpace <= 0) return slice;
      return slice.slice(0, lastSpace);
    };

    const getTruncateLimit = () => {
      if (typeof window === 'undefined' || !window.matchMedia) return DESKTOP_TRUNCATE;
      return window.matchMedia('(min-width: 768px)').matches ? DESKTOP_TRUNCATE : MOBILE_TRUNCATE;
    };

    const createNoteElement = (rawNote, modifierClass) => {
      const fullNote = String(rawNote).trim().slice(0, MAX_NOTE_CHARS);

      const wrap = document.createElement('div');
      wrap.className = 'course-section-note ' + (modifierClass || '');
      wrap.dataset.fullNote = fullNote;
      const textSpan = document.createElement('span');
      textSpan.className = 'course-section-note__text';
      const linkSpan = document.createElement('span');
      linkSpan.className = 'course-section-note__toggle';

      const setContent = (expanded) => {
        const displayLimit = getTruncateLimit();
        const displayTruncated = truncateAtBlank(fullNote, displayLimit);
        const showFull = expanded || fullNote.length <= displayLimit;
        const displayText = showFull ? fullNote : displayTruncated;
        const showLink = fullNote.length > displayLimit;
        textSpan.textContent = displayText;
        linkSpan.innerHTML = '';
        linkSpan.style.display = 'none';
        if (showLink) {
          const link = document.createElement('button');
          link.type = 'button';
          link.className = 'course-section-note__toggle-link';
          link.setAttribute('aria-expanded', showFull ? 'true' : 'false');
          if (showFull) {
            link.textContent = ' Show Less';
            link.addEventListener('click', () => setContent(false));
          } else {
            link.textContent = ' Show More';
            link.addEventListener('click', () => setContent(true));
          }
          linkSpan.style.display = 'inline';
          linkSpan.appendChild(link);
        }
      };

      setContent(false);
      wrap.appendChild(textSpan);
      wrap.appendChild(linkSpan);
      return wrap;
    };

    const sectionCourseNotes = document.createElement('div');
    sectionCourseNotes.className = 'course-section-notes';
    if (isValidNote(sectionNote)) {
      sectionCourseNotes.appendChild(createNoteElement(sectionNote, 'course-section-note--section'));
    }
    if (isValidNote(courseNote)) {
      sectionCourseNotes.appendChild(createNoteElement(courseNote, 'course-section-note--course'));
    }

    // Helper function to generate remove button HTML
    const getRemoveButtonHTML = () => `
      <button class="remove-btn" onclick="removeCourse(this)" 
              data-campus="${sectionData.courseInfo.campus ?? ''}" 
              data-term="${sectionData.courseInfo.term ?? ''}" 
              data-division="${sectionData.courseInfo.division ?? ''}" 
              data-department="${sectionData.courseInfo.department}" 
              data-course="${sectionData.courseInfo.course}" 
              data-section="${sectionData.courseInfo.section}">
          Remove
          <svg class="remove-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
      </button>
    `;

    // Helper function to generate access program banner HTML
    const getAccessProgramBannerHTML = (sectionNote) => {
      const label = sectionNote || 'Follett Access';
      const accessProgramTitleSuffix = /\bprogram\b/i.test(String(label))
        ? ''
        : ' PROGRAM';

      const tooltipMessage =
        'This program gives you access to all your required course materials by the first day of class for a one-time affordable fee that\'s already included in your tuition. If you don\'t want to participate, you may opt out.';

      // Shared icon
      const icon = `
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M8.03751 3.23346C7.81155 3.23346 7.61727 3.41091 7.61727 3.6432V3.9899H7.49033C7.1739 3.9899 6.8716 4.11407 6.64959 4.33328C6.42775 4.55234 6.30402 4.84839 6.30402 5.15606C6.30402 5.46373 6.42775 5.75978 6.64959 5.97884C6.8716 6.19806 7.1739 6.32223 7.49033 6.32223H7.61727V7.01562H6.72426C6.4983 7.01562 6.30402 7.19307 6.30402 7.42536C6.30402 7.65765 6.4983 7.83509 6.72426 7.83509H7.61727V8.18179C7.61727 8.41408 7.81155 8.59153 8.03751 8.59153C8.26348 8.59153 8.45775 8.41408 8.45775 8.18179V7.83509H8.5847C8.90113 7.83509 9.20342 7.71092 9.42543 7.4917C9.64728 7.27265 9.771 6.9766 9.771 6.66893C9.771 6.36126 9.64728 6.06521 9.42543 5.84615C9.20342 5.62693 8.90113 5.50276 8.5847 5.50276H8.45775V4.80936H9.13189C9.35786 4.80936 9.55213 4.63192 9.55213 4.39963C9.55213 4.16734 9.35786 3.9899 9.13189 3.9899H8.45775V3.6432C8.45775 3.41091 8.26348 3.23346 8.03751 3.23346ZM7.24768 4.90908C7.31298 4.84461 7.40037 4.80936 7.49033 4.80936H7.61727V5.50276H7.49033C7.40037 5.50276 7.31298 5.46752 7.24768 5.40304C7.18218 5.33836 7.1445 5.2496 7.1445 5.15606C7.1445 5.06252 7.18218 4.97376 7.24768 4.90908ZM8.45775 7.01562V6.32223H8.5847C8.67465 6.32223 8.76205 6.35747 8.82734 6.42195C8.89285 6.48663 8.93052 6.57539 8.93052 6.66893C8.93052 6.76247 8.89285 6.85123 8.82734 6.91591C8.76205 6.98038 8.67465 7.01562 8.5847 7.01562H8.45775Z" fill="#204A65"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.61333 12.6985C9.61333 12.9741 9.39751 13.1976 9.13129 13.1976H6.85164C6.58542 13.1976 6.3696 12.9741 6.3696 12.6985C6.3696 12.4229 6.58542 12.1995 6.85164 12.1995H9.13129C9.39751 12.1995 9.61333 12.4229 9.61333 12.6985Z" fill="#204A65"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M9.18567 9.08436L9.68398 8.7961C10.6952 8.21114 11.3719 7.12026 11.3719 5.8725C11.3719 4.00885 9.86114 2.49807 7.9975 2.49807C6.13385 2.49807 4.62307 4.00885 4.62307 5.8725C4.62307 7.12026 5.29978 8.21114 6.31101 8.7961L6.80932 9.08435V10.6094C6.80932 10.6619 6.85187 10.7044 6.90437 10.7044H9.09062C9.14312 10.7044 9.18567 10.6619 9.18567 10.6094V9.08436ZM10.1837 9.66004V10.6094C10.1837 11.2131 9.69433 11.7025 9.09062 11.7025H6.90437C6.30065 11.7025 5.81125 11.2131 5.81125 10.6094V9.66003C4.50431 8.90401 3.625 7.49094 3.625 5.8725C3.625 3.45763 5.58263 1.5 7.9975 1.5C10.4124 1.5 12.37 3.45763 12.37 5.8725C12.37 7.49094 11.4907 8.90401 10.1837 9.66004Z" fill="#204A65"/>
        </svg>
      `;

      return `
        <div class="rental-banner access-program-style">
          <span class="tooltip-trigger access-program-tooltip" tabindex="0">
            ${icon}
            <span class="access-program-text">Covered by ${label}</span>
            <span class="access-program-tooltip-content" role="tooltip" aria-hidden="true">
              <span class="access-program-tooltip-header">
                ${icon}
                <span class="access-program-tooltip-title">
                  THIS TERM IS PART OF THE <span class="access-program-title">${label}</span>${accessProgramTitleSuffix}
                </span>
              </span>
              <span class="access-program-tooltip-body">
                ${tooltipMessage}
              </span>
            </span>
          </span>
        </div>
      `;
    };

    // Find section access data
    const filteredSectionAccess = ProductDisplayManager._sectionsById?.get(
      String(sectionData.courseInfo.selectedsectionId ?? '')
    );

    const terms = Array.isArray(window.courseData?.ddcs_term)
      ? window.courseData.ddcs_term
      : [];
    const campuses = Array.isArray(window.courseData?.ddcs_campus_entity)
      ? window.courseData.ddcs_campus_entity
      : [];
    const filteredTermSelected = terms.find(
      term => term.term_id === sectionData.courseInfo.termId
    );
    const campusEntityId =
      filteredTermSelected?.campus_entity_id ||
      filteredSectionAccess?.campus_entity_id ||
      '';
    const filteredAccessProgramName = campuses.find(
      campus => campus.campus_entity_id === campusEntityId
    );
    
    const hasAccessProgram = filteredSectionAccess?.included_enabled_flag;
    const includedProgramName =
      typeof filteredAccessProgramName?.included_program_name === 'string'
        ? filteredAccessProgramName.included_program_name.trim()
        : '';
    const accessProgramNote = includedProgramName || filteredSectionAccess?.included_section_note;
    
    // Build course title HTML
    const courseTitleHTML = `<div class="course-title">${sectionData.courseInfo.department} ${sectionData.courseInfo.course}</div>`;
    
    // Build course details HTML
    const courseDetailsHTML = hasAccessProgram
      ? `
        <div class="course-access">
          ${courseTitleHTML}
          ${getAccessProgramBannerHTML(accessProgramNote)}
        </div>
      `
      : courseTitleHTML;

    const requiredLabel =
      requiredCount === 1
        ? '1 required material'
        : `${requiredCount} required materials`;

    // Generate header HTML (structure matches CMRP Figma: mobile stacks title → count → section; desktop uses grid)
    headerSection.innerHTML = `
      <div class="header-content">
        <div class="course-details">
          ${courseDetailsHTML}
          <div class="required-count">${requiredLabel}</div>
          <div class="section-info">Section: ${sectionNumber} | Instructor: ${instructorName}</div>
        </div>
        <div class="header-actions">
          ${getRemoveButtonHTML()}
        </div>
      </div>
    `;
    courseCard.appendChild(headerSection);
    if (sectionCourseNotes.children.length > 0) {
      courseCard.appendChild(sectionCourseNotes);
    }

    const materialsSection = await this.createMaterialsSection(sectionData, choice_minimum, department);
    courseCard.appendChild(materialsSection);
    section.appendChild(courseCard);
    productsContainer.appendChild(section);

    // Trigger event that materials are loaded
    document.dispatchEvent(new CustomEvent('materialsLoaded'));

    // Update In Bag indicators after materials are loaded
    this.updateInBagIndicatorsForSection();
    this.updatePurchaseOptions();
    ProductDisplayManager._perfEnd(perfDept, {
      products: sectionData.products?.length ?? 0,
    });
  }
  updatePurchaseOptions(){
    const currentUrl = new URL(window.location.href);
    const shopByValue = currentUrl.searchParams.get('shopBy');
    const checkboxLabels = document.querySelectorAll('.checkbox-label');
    const removebtn = document.querySelectorAll('.remove-btn');
    if (shopByValue === 'discoverview') {    
      checkboxLabels.forEach(label => {    
        // @ts-ignore
        label.style.display = 'none';
      });
      removebtn.forEach(label => {    
        // @ts-ignore
        label.style.display = 'none';
      });
    }else{
      checkboxLabels.forEach(label => {    
        // @ts-ignore
        label.style.display = 'inline-block';
      });
      removebtn.forEach(label => {    
        // @ts-ignore
        label.style.display = 'flex';
      });
    }
  }
  /**
   * Create materials section for a course
   * @param {any} sectionData - Section data
   * @returns {HTMLElement} Materials section element
   * @private
   */
  async createMaterialsSection(sectionData, choice_minimum, department = '') {
    const materialsSection = document.createElement('div');
    materialsSection.className = 'materials-section';

    if (sectionData.products.length === 0) {
        const noteText = sectionData._cmrpSamOnlyAllSam
          ? ProductDisplayManager.getCmrpSamHiddenMessage()
          : "Materials aren't required for this course.";
        const adoptionStyle = document.createElement('div');
        adoptionStyle.className = 'no-materials-message';
        adoptionStyle.innerHTML = `
            <div class="adoption-message-content">
                <div class="adoption-icon">${this.ADOPTION_STATUS_ICON}</div>
                <div class="adoption-text">
                    <h4 class="adoption-title">Course Materials Status</h4>
                    <p class="adoption-note"></p>
                </div>
            </div>
        `;
        const noteEl = adoptionStyle.querySelector('.adoption-note');
        if (noteEl) {
          noteEl.textContent = noteText;
        }
        materialsSection.appendChild(adoptionStyle);
        return materialsSection;
    }

    const courseInfo = sectionData.courseInfo;
    const matchesSectionRow = p =>
      ProductDisplayManager._productRowMatchesCourseSectionIds(p, courseInfo);

    // Separate suggested materials from other materials
    const suggestedProducts = sectionData.products.filter(
        /** @param {any} p */ p =>
            p.material_type === 'Suggested' && matchesSectionRow(p)
    );

    const choiceProducts = sectionData.products.filter(
        /** @param {any} p */ p => (
            p.material_type === 'Choice' ||
            p.material_type === 'Choice/Recommended' ||
            p.material_type === 'Choice/Required')
            &&
            matchesSectionRow(p)
    );

    const visibleProducts = sectionData.products.filter(
      /** @param {any} p */ p =>
          (p.material_type === 'Required' ||
          p.material_type === 'Recommended' ||            
          !p.material_type ||
          p.material_type.trim() === '') &&
          p.material_type !== 'Suggested' &&
          p.material_type !== 'Choice' &&
          p.material_type !== 'Choice/Recommended' &&
          p.material_type !== 'Choice/Required' // Exclude suggested/choice from main list
    );
    const filteredItems = visibleProducts.filter(matchesSectionRow);
    
    const materialOrder = {
      "Required": 1,      
      "Recommended": 2     
    };

    
    
    const reorderedProducts = filteredItems.sort((a, b) => {
        const aOrder = materialOrder[a.material_type] || 4; // 4 para tipos desconhecidos
        const bOrder = materialOrder[b.material_type] || 4;

        return aOrder - bOrder; 
    });

    let dataProducts;
    
    if (window.shopifySettings && window.shopifySettings.unifiedViewCMRP) {
      let filteredProducts = filteredItems.reduce((acc, product) => {
      
        if (product.metafields.book_type_ind === "CEB") {
          const printRelationshipISBN = product.metafields.print_relationship_isbn;
          if (printRelationshipISBN && printRelationshipISBN !== "") {
            const isbnArray = JSON.parse(printRelationshipISBN);
            const shouldRemove = isbnArray.some(isbn => {
                return filteredItems.some(item => 
                    item.metafields.isbn_ean === isbn &&
                    item.material_type === product.material_type 
                );
            });
            if (shouldRemove) {
              const variantsToAdd = product.variants || [];
              const parentProduct = filteredItems.find(item => 
                  item.metafields.book_type_ind === "TXT" && 
                  item.material_type === product.material_type && 
                  isbnArray.some(isbn => item.metafields.isbn_ean === isbn) 
              );
              if (parentProduct) {
                parentProduct.variants = [...(parentProduct.variants || []), ...variantsToAdd];
              }
              return acc; 
            }
          }
          //Unified View CMRP (Digital addition of all products with the same ISBN_EAN code within the same product's digital tab)
          /* FM-1488 - Commenting to avoid unifying digital products on the CMRP page at the request of the Follett team on 03/10.
          const isbn_ean = product.metafields.isbn_ean;
          if(isbn_ean && isbn_ean !== ""){
            const shouldRemove = window.courseData.products_Materials.some(item => 
              item.metafields.isbn_ean === isbn_ean && 
              item.material_type === product.material_type
            );
            if (shouldRemove && idProductFilt > 0) {
              const variantsToAdd = product.variants || [];
              const parentProduct = window.courseData.products_Materials.find(item => 
                item.metafields.book_type_ind === "CEB" && 
                item.material_type === product.material_type && 
                item.metafields.isbn_ean === isbn_ean
              );
              if (parentProduct) {
                parentProduct.variants = [...(parentProduct.variants || []), ...variantsToAdd];
              }
              idProductFilt++;
              return acc; 
            }else{
              idProductFilt++;
            }
            
          }
          */
          
        }
        acc.push(product);
        return acc;
      }, []);

      dataProducts = filteredProducts
    }else{
      dataProducts = filteredItems
    }

    const choiceMaterialTypes = [
      'Choice',
      'Choice/Required',
      'Choice/Recommended',
    ];
    const visibleChoiceProducts = dataProducts.filter(
      /** @param {any} p */ p => choiceMaterialTypes.includes(p.material_type)
    );
    if (visibleChoiceProducts.length > 0) {
      const choiceInstruction = document.createElement('div');
      choiceInstruction.className = 'choice-instruction';
      choiceInstruction.innerHTML = `
            <div class="choice-message">
                This course includes a choice option allowing you to select the items you want. Choose ${choice_minimum} out of ${visibleChoiceProducts.length}.
            </div>
        `;
      materialsSection.appendChild(choiceInstruction);
    }

    console.log("Unified Products to display ==>", dataProducts)

    
    const filteredProductsSC = dataProducts
      .map(product => {
        const productSystem = window.ProductSystem || {};
        const hasSc =
          productSystem.salesChannelTokensContainSc ||
          this.productHandler.salesChannelTokensContainSc.bind(this.productHandler);
        const sectionIdNumber = sectionData?.courseInfo?.selectedsectionId;

        const variantIsCEBCourseMaterials = variant => {
          const mf = variant?.metafields;
          if (Array.isArray(mf)) {
            return mf.some(
              x =>
                x &&
                x.namespace === 'course_materials' &&
                (x.book_type_ind === 'CEB' ||
                  (x.key === 'book_type_ind' && String(x.value) === 'CEB'))
            );
          }
          if (mf && typeof mf === 'object') {
            return (
              (mf.book_type_ind === 'CEB' && mf.namespace === 'course_materials') ||
              mf.course_materials?.book_type_ind === 'CEB'
            );
          }
          return false;
        };

        // Sales Channel rule applied to digital CEB products and "no format" products (options: ["Type"]).
        const options = Array.isArray(product?.options) ? product.options : [];
        const isNoFormatTypeProduct =
          options.length === 1 &&
          String(
            typeof options[0] === 'object' && options[0] !== null
              ? (options[0].name ?? options[0].value ?? '')
              : options[0]
          ).trim() === "Type";

        if (product?.metafields?.book_type_ind !== "CEB" && !isNoFormatTypeProduct) {
          if(product.metafields.book_type_ind === "TXT"){
            const variants = product.variants || [];            
            const hasCEBVariant = variants.some(variantIsCEBCourseMaterials);
            if(hasCEBVariant){
              let mapped_section = null;
              if (product.metafields?.course_materials?.section_data_mapping) {
                try {
                  const section_data_mapping = JSON.parse(product.metafields.course_materials.section_data_mapping);
                  if (Array.isArray(section_data_mapping) && section_data_mapping.length > 0) {
                    mapped_section = section_data_mapping.find(
                      item => String(item?.SectionID) === String(sectionIdNumber)
                    );
                  }
                } catch (error) {
                  console.error("Error parsing section_data_mapping:", error);
                }
              }
              const accessProgramSection =
                mapped_section &&
                (mapped_section.IncludEDMaterialFlag === "true" || mapped_section.IncludEDMaterialFlag === true);

              if (!accessProgramSection) {
                return {
                  ...product,
                  hideSalesChannel: false
                };
              }

              const keptVariants = Array.isArray(product.variants)
                ? product.variants.filter(variant => {
                    if (!variantIsCEBCourseMaterials(variant)) {
                      return true;
                    }
                    const mf = variant?.metafields;
                    let cmDigitalRaw = '';
                    let customRaw = '';

                    if (Array.isArray(mf)) {
                      const cmDigitalRow = mf.find(
                        x =>
                          x &&
                          x.namespace === 'cm_digital' &&
                          (x.key === 'sales_channel' || x.sales_channel != null)
                      );
                      if (cmDigitalRow?.sales_channel != null) {
                        cmDigitalRaw = String(cmDigitalRow.sales_channel);
                      }

                      const customRow = mf.find(
                        x =>
                          x &&
                          x.namespace === 'custom' &&
                          (x.key === 'sales_channel' || x.sales_channel != null)
                      );
                      if (customRow?.sales_channel != null) {
                        customRaw = String(customRow.sales_channel);
                      }
                    } else if (mf && typeof mf === 'object') {
                      if (mf.cm_digital?.sales_channel != null) {
                        cmDigitalRaw = String(mf.cm_digital.sales_channel);
                      }
                      if (mf.custom?.sales_channel != null) {
                        customRaw = String(mf.custom.sales_channel);
                      }
                    }

                    if (cmDigitalRaw) return hasSc(cmDigitalRaw);
                    if (customRaw) return hasSc(customRaw);
                    return false;
                  })
                : [];
              console.log("keptVariants print prodct with CEB variant ==>", keptVariants)
              const allDigitalVariantsWithoutSc = keptVariants.length === 0;
                
              return {
                ...product,
                variants: keptVariants,
                hideSalesChannel: allDigitalVariantsWithoutSc
              };
            }else{
              return {
                ...product,
                hideSalesChannel: false
              };
            }
          }else{
            return {
              ...product,
              hideSalesChannel: false
            };
          }
          
        }

        let mapped_section = null;
        if (product.metafields?.course_materials?.section_data_mapping) {
          try {
            const section_data_mapping = JSON.parse(product.metafields.course_materials.section_data_mapping);
            if (Array.isArray(section_data_mapping) && section_data_mapping.length > 0) {
              mapped_section = section_data_mapping.find(
                item => String(item?.SectionID) === String(sectionIdNumber)
              );
            }
          } catch (error) {
            console.error("Error parsing section_data_mapping:", error);
          }
        }

        const accessProgramSection =
          mapped_section &&
          (mapped_section.IncludEDMaterialFlag === "true" || mapped_section.IncludEDMaterialFlag === true);

        if (!accessProgramSection) {
          return {
            ...product,
            hideSalesChannel: false
          };
        }

        const keptVariants = Array.isArray(product.variants)
          ? product.variants.filter(variant => {
              if (!variantIsCEBCourseMaterials(variant)) {
                return true;
              }
              const mf = variant?.metafields;
              let cmDigitalRaw = '';
              let customRaw = '';

              if (Array.isArray(mf)) {
                const cmDigitalRow = mf.find(
                  x =>
                    x &&
                    x.namespace === 'cm_digital' &&
                    (x.key === 'sales_channel' || x.sales_channel != null)
                );
                if (cmDigitalRow?.sales_channel != null) {
                  cmDigitalRaw = String(cmDigitalRow.sales_channel);
                }

                const customRow = mf.find(
                  x =>
                    x &&
                    x.namespace === 'custom' &&
                    (x.key === 'sales_channel' || x.sales_channel != null)
                );
                if (customRow?.sales_channel != null) {
                  customRaw = String(customRow.sales_channel);
                }
              } else if (mf && typeof mf === 'object') {
                if (mf.cm_digital?.sales_channel != null) {
                  cmDigitalRaw = String(mf.cm_digital.sales_channel);
                }
                if (mf.custom?.sales_channel != null) {
                  customRaw = String(mf.custom.sales_channel);
                }
              }

              if (cmDigitalRaw) return hasSc(cmDigitalRaw);
              if (customRaw) return hasSc(customRaw);
              return false;
            })
          : [];
        console.log("keptVariants ==>", keptVariants)
        const allDigitalVariantsWithoutSc = keptVariants.length === 0;
          
        return {
          ...product,
          variants: keptVariants,
          hideSalesChannel: allDigitalVariantsWithoutSc
        };
      });
    const sanitizedDept = String(department).replace(/[^a-zA-Z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 's';
    const bundleDisplayList = this.buildBundleDisplayList(filteredProductsSC);
    console.log("Products by access SC ==>", filteredProductsSC)
    for (let i = 0; i < bundleDisplayList.length; i++) {
        const row = bundleDisplayList[i];
        const { parentProduct, childProducts } = row;
        const instanceId = `${sanitizedDept}-${i}`;
        const materialItem = await this.createMaterialItem(parentProduct, sectionData, instanceId, { childProducts });
        //console.log("materialItem ==>", materialItem)
        materialsSection.appendChild(materialItem);

        
        /** @type {any} */
        const globalWindow = window;
        if (typeof globalWindow.addMaterialEventListeners === 'function') {
            globalWindow.addMaterialEventListeners(materialItem, parentProduct);
        }
    }

    console.log("choiceProducts ==> ", choiceProducts);

    
    if (choiceProducts.length > 0) {
      const isMobile = window.innerWidth <= 786;
      const materialItemChoice = document.createElement('div');
      materialItemChoice.className = 'choice-list choice-products-section';
      if (!isMobile) {
        materialItemChoice.classList.add('material-item');
      }

      const choiceCompleted = document.createElement('div');
      choiceCompleted.classList.add('choice-complete', 'hide-choice-completed');    
      choiceCompleted.id = 'choice-completed-'+sectionData.courseInfo.selectedsectionId;
      choiceCompleted.innerHTML = `
          <div class="choice-message choice-completed">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M16.0312 9C16.0312 5.11675 12.8833 1.96875 9 1.96875C5.11675 1.96875 1.96875 5.11675 1.96875 9C1.96875 12.8833 5.11675 16.0312 9 16.0312C12.8833 16.0312 16.0312 12.8833 16.0312 9ZM16.9688 9C16.9688 13.401 13.401 16.9688 9 16.9688C4.59898 16.9688 1.03125 13.401 1.03125 9C1.03125 4.59898 4.59898 1.03125 9 1.03125C13.401 1.03125 16.9688 4.59898 16.9688 9Z" fill="#1F7427"/>
                <path d="M10.9922 7.10815C11.1741 6.98819 11.4217 7.0081 11.5818 7.16821C11.7419 7.32832 11.7618 7.57589 11.6418 7.75781L11.5818 7.83179L8.58179 10.8318C8.39873 11.0148 8.10127 11.0148 7.91821 10.8318L6.41821 9.33179L6.35815 9.25781C6.23819 9.07589 6.2581 8.82832 6.41821 8.66821C6.57832 8.5081 6.82589 8.48819 7.00781 8.60815L7.08179 8.66821L8.25 9.83643L10.9182 7.16821L10.9922 7.10815Z" fill="#1F7427"/>
              </svg> All set! You've completed your choice material selections.
          </div>
      `;
      materialItemChoice.appendChild(choiceCompleted);

      const choiceExtraData = document.createElement('div');
      choiceExtraData.classList.add('choice-extra-data', 'hide-choice-extra-data');    
      choiceExtraData.id = 'choice-extra-data-'+sectionData.courseInfo.selectedsectionId;
      choiceExtraData.innerHTML = `
          <div class="choice-message rental-banner">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M16.0312 9C16.0312 5.11675 12.8833 1.96875 9 1.96875C5.11675 1.96875 1.96875 5.11675 1.96875 9C1.96875 12.8833 5.11675 16.0312 9 16.0312C12.8833 16.0312 16.0312 12.8833 16.0312 9ZM16.9688 9C16.9688 13.401 13.401 16.9688 9 16.9688C4.59898 16.9688 1.03125 13.401 1.03125 9C1.03125 4.59898 4.59898 1.03125 9 1.03125C13.401 1.03125 16.9688 4.59898 16.9688 9Z" fill="#2B51A2"/>
                <path d="M8.53125 12V9C8.53125 8.74112 8.74112 8.53125 9 8.53125C9.25888 8.53125 9.46875 8.74112 9.46875 9V12C9.46875 12.2589 9.25888 12.4688 9 12.4688C8.74112 12.4688 8.53125 12.2589 8.53125 12Z" fill="#2B51A2"/>
                <path d="M9.00732 5.53125L9.10181 5.54077C9.31549 5.58442 9.47607 5.77342 9.47607 6C9.47607 6.22658 9.31549 6.41558 9.10181 6.45923L9.00732 6.46875H9C8.74112 6.46875 8.53125 6.25888 8.53125 6C8.53125 5.74112 8.74112 5.53125 9 5.53125H9.00732Z" fill="#2B51A2"/>
              </svg> Heads up, you've selected more items than your instructor requires for this course.
          </div>
      `;
      materialItemChoice.appendChild(choiceExtraData);
        
      const choiceInstruction = document.createElement('div');
      choiceInstruction.className = 'choice-instruction';
      choiceInstruction.innerHTML = `
          <input type="hidden" id="choice-minimum-${sectionData.courseInfo.selectedsectionId}" value="${choice_minimum}" />
          <div class="choice-products-header">
            <span class="choice-products-header__badge">Required</span>
            <p class="choice-products-header__hint">*Choose ${choice_minimum} out of ${choiceProducts.length} materials</p>
          </div>
      `;
      materialItemChoice.appendChild(choiceInstruction);

      const choiceItems = document.createElement('div');
      choiceItems.id = 'choice-items-'+sectionData.courseInfo.selectedsectionId;
      choiceItems.className = 'choice-products-grid';

      for (let i = 0; i < choiceProducts.length; i++) {
        const product = choiceProducts[i];
        const instanceId = `${sanitizedDept}-choice-${i}`;
        const currentUrl = window.location.href;
        let materialItem;
        if (currentUrl.includes('/pages/follett-discover-view')) {
          materialItem = await this.createMaterialItem(product, sectionData, instanceId);
        } else {
          materialItem = await this.createMaterialItemChoice(product, sectionData, instanceId);
        }
        choiceItems.appendChild(materialItem);

        
        /** @type {any} */
        const globalWindow = window;
        if (typeof globalWindow.addMaterialEventListeners === 'function') {
            globalWindow.addMaterialEventListeners(materialItem, product);
        }
      }

      materialItemChoice.appendChild(choiceItems);
      if (!isMobile) {
        const choiceFinalItems = document.createElement('div');
        choiceFinalItems.id = 'choice-final-items-'+sectionData.courseInfo.selectedsectionId; 
        choiceFinalItems.classList.add('hide-choice-items');
        

        await this.renderChoiceMaterialsComponent(choiceFinalItems, choiceProducts, sectionData);

        materialItemChoice.appendChild(choiceFinalItems);
      }
      materialsSection.appendChild(materialItemChoice);

      
      
    }
    
    
    // Render suggested materials component at the bottom, after all other materials
    if (suggestedProducts.length > 0) {
      await this.renderSuggestedMaterialsComponent(materialsSection, suggestedProducts, sectionData);
    }

    return materialsSection;
  }
  /**
   * Render Choice materials using Preact component
   * @param {HTMLElement} materialsSection - Parent materials section container
   * @param {Array<any>} choiceProducts - Array of choice products
   * @param {Object} sectionData - Section data
   * @private
   */
  async renderChoiceMaterialsComponent(materialItemChoice, choiceProducts, sectionData) {
    // Ensure window.createMaterialItemHTML is available for the Preact component
    // Store reference to 'this' for use in the function
    const self = this;
    if (typeof window !== 'undefined' && !window.createMaterialItemHTML) {
      /** @type {any} */
      const globalWindow = window;
      globalWindow.createMaterialItemHTML = async function(product, sectionData) {
        // Create a wrapper div and get the HTML
        const wrapper = document.createElement('div');
        const html = await self.renderMaterialItemTemplate(product, sectionData);
        wrapper.innerHTML = html;
        return wrapper.innerHTML;
      };
    }

    // Create the custom element
    // The component module should be loaded by the entry point (preact-theme.js)
    // preact-custom-element will automatically handle rendering when the element is added to DOM
    const choiceComponent = document.createElement('choice-products-component');
    
    // Set attributes with JSON stringified data
    const choiceProductsJson = JSON.stringify(choiceProducts);
    choiceComponent.setAttribute('products-data', choiceProductsJson);
    choiceComponent.setAttribute('section-data', JSON.stringify(sectionData));
    choiceComponent.setAttribute('price-data', JSON.stringify([]));
    choiceComponent.dataset.cmrpProducts = choiceProductsJson;
    
    // Append to materials section at the end (after all other material items)
    // Using appendChild ensures it's added as the last child
    materialItemChoice.appendChild(choiceComponent);
  }
  /**
   * Render suggested materials using Preact component
   * @param {HTMLElement} materialsSection - Parent materials section container
   * @param {Array<any>} suggestedProducts - Array of suggested products
   * @param {Object} sectionData - Section data
   * @private
   */
  async renderSuggestedMaterialsComponent(materialsSection, suggestedProducts, sectionData) {
    // Ensure window.createMaterialItemHTML is available for the Preact component
    // Store reference to 'this' for use in the function
    const self = this;
    if (typeof window !== 'undefined' && !window.createMaterialItemHTML) {
      /** @type {any} */
      const globalWindow = window;
      globalWindow.createMaterialItemHTML = async function(product, sectionData) {
        // Create a wrapper div and get the HTML
        const wrapper = document.createElement('div');
        const html = await self.renderMaterialItemTemplate(product, sectionData);
        wrapper.innerHTML = html;
        return wrapper.innerHTML;
      };
    }

    // Create the custom element
    // The component module should be loaded by the entry point (preact-theme.js)
    // preact-custom-element will automatically handle rendering when the element is added to DOM
    const suggestedComponent = document.createElement('suggested-materials-component');
    
    // Set attributes with JSON stringified data
    const suggestedProductsJson = JSON.stringify(suggestedProducts);
    suggestedComponent.setAttribute('products-data', suggestedProductsJson);
    suggestedComponent.setAttribute('section-data', JSON.stringify(sectionData));
    suggestedComponent.dataset.cmrpProducts = suggestedProductsJson;
    
    // Append to materials section at the end (after all other material items)
    // Using appendChild ensures it's added as the last child
    materialsSection.appendChild(suggestedComponent);
  }
  getPriceRangeForProducts(choiceProduct) {
    if (!choiceProduct.variants) return { title: choiceProduct.title, priceRange: 'N/A' };
        
    const availableVariants = choiceProduct.variants.filter(variant => variant.available);

    
    if (availableVariants.length === 0) return { title: choiceProduct.title, priceRange: 'N/A' };

    const prices = availableVariants
        .map(variant => parseFloat(variant.price))
        .filter(price => !isNaN(price));

    
    const minPrice = Math.min(...prices);
    const maxPrice = Math.max(...prices);

    return {
      title: choiceProduct.title,
      priceRange: `$${minPrice.toFixed(2)} - $${maxPrice.toFixed(2)}`
    };
  }
  /**
   * Render material item template (mimics Liquid snippet)
   * @param {any} product - Product data
   * @returns {string} HTML string
   * @private
   */
  // @ts-ignore
  async renderMaterialItemTemplateChoice(product, sectionData, instanceId = '') {
    // Extract product data
   
    const authorName = this.productHandler.extractAuthorName(product) || '';
    const edition = this.productHandler.extractEditionFromTitle(product) || 'Not specified';
    const isbn = this.productHandler.extractISBNFromProduct(product) || 'Not specified';
    // @ts-ignore
    const formatButtons = this.productHandler.generateFormatButtons(product);
    
    
    const prod_formats = this.productHandler.getVariantUniques(product);
    // Badge HTML (conditional)
    

    let htmlOutput;

    const showDigitalDeliveryNotice =
      await ProductDisplayManager.getDigitalDeliveryNoticeVisible();
    const digitalDeliveryNoticeHtml = showDigitalDeliveryNotice
      ? '<div class="digital-delivery-notice">*Digital delivery fees may apply</div>'
      : '';

    // Check if product has rental options
    const hasRentalOptions = this.hasRentalOptions(product);
    
    if (prod_formats.length === 1) {
        if (prod_formats[0] === "Digital") {
            const initialPurchaseOptions = await this.productHandler.generateInitialPurchaseOptions("Digital", product, sectionData, instanceId);
            const rentalBanner = hasRentalOptions ? `
                        <div class="rental-banner">
                            <span class="dollar-icon">$</span>
                            Rent for the semester and save money!
                        </div>` : '';
            const digitalPrice = this.getLowestPriceForFormat(product, "Digital");
            htmlOutput = `
        <div class="purchase-options-container">
            <div class="format-tabs">
                <button class="format-tab active" data-format="digital" onclick="window.toggleFormatCollapse(this)">
                    <span class="format-tab-title">Digital</span>
                    <span class="format-tab-price">${digitalPrice}</span>
                </button>
            </div>
            <div class="format-content-area">
                <div class="format-collapse active" data-format="digital" style="display: block;">
                    <div class="collapse-content">
                        ${rentalBanner}
                        <div class="purchase-types purchase-types-CMRP">
                            ${initialPurchaseOptions}
                        </div>
                        ${digitalDeliveryNoticeHtml}
                    </div>
                </div>
            </div>
            
        </div>
        <div class="material-notify-container" style="display: none;">
            <!-- Notify me button will be added here when needed -->
        </div>`;
        } else if (prod_formats[0] === "Print") {
            const print = await this.productHandler.generateInitialPurchaseOptions("Print", product, sectionData, instanceId);
            const rentalBanner = hasRentalOptions ? `
                        <div class="rental-banner">
                            <span class="dollar-icon">$</span>
                            Rent for the semester and save money!
                        </div>` : '';
            const printPrice = this.getLowestPriceForFormat(product, "Print");
            htmlOutput = `
        <div class="purchase-options-container">
            <div class="format-tabs">
                <button class="format-tab active" data-format="print" onclick="window.toggleFormatCollapse(this)">
                    <span class="format-tab-title">Print</span>
                    <span class="format-tab-price">${printPrice}</span>
                </button>
            </div>
            <div class="format-content-area">
                <div class="format-collapse active" data-format="print" style="display: block;">
                    <div class="collapse-content">
                        ${rentalBanner}
                        <div class="purchase-types purchase-types-CMRP">
                            ${print}
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="material-notify-container" style="display: none;">
            <!-- Notify me button will be added here when needed -->
        </div>`;
        } else {
          const initialPurchaseOptions = await this.productHandler.generateInitialPurchaseOptions("", product, sectionData, instanceId);
          htmlOutput = `
            <!-- Simple product - direct purchase options -->
            <div class="simple-product-purchase">
              <div class="format-content-area">
                  <div class="format-collapse active" style="display: block;">
                      <div class="collapse-content">
                          <div class="purchase-types purchase-types-CMRP">
                            ${initialPurchaseOptions}
                          </div>
                      </div>
                  </div>
              </div>
            </div>
            <div class="material-notify-container" style="display: none;">
              <!-- Notify me button will be added here when needed -->
            </div>
              `;
        }
    } else if (prod_formats.length === 2 && prod_formats.includes("Digital") && prod_formats.includes("Print")) {
        const digital = await this.productHandler.generateInitialPurchaseOptions("Digital", product, sectionData, instanceId);
        const print = await this.productHandler.generateInitialPurchaseOptions("Print", product, sectionData, instanceId);
        const rentalBanner = hasRentalOptions ? `
                        <div class="rental-banner">
                            <span class="dollar-icon">$</span>
                            Rent for the semester and save money!
                        </div>` : '';
        const digitalPrice = this.getLowestPriceForFormat(product, "Digital");
        const printPrice = this.getLowestPriceForFormat(product, "Print");
        console.log("prod_formats", prod_formats)
        htmlOutput = `
        <div class="purchase-options-container">
            <div class="format-tabs">
                <button class="format-tab active" data-format="digital" onclick="window.toggleFormatCollapse(this)">
                    <span class="format-tab-title">Digital</span>
                    <span class="format-tab-price">${digitalPrice}</span>
                </button>
                <button class="format-tab" data-format="print" onclick="window.toggleFormatCollapse(this)">
                    <span class="format-tab-title">Print</span>
                    <span class="format-tab-price">${printPrice}</span>
                </button>
            </div>
            <div class="format-content-area">
                <div class="format-collapse active" data-format="digital" style="display: block;">
                    <div class="collapse-content">
                        ${rentalBanner}
                        <div class="purchase-types purchase-types-CMRP">
                            ${digital}
                        </div>
                        ${digitalDeliveryNoticeHtml}
                    </div>
                </div>
                
                <div class="format-collapse" data-format="print" style="display: none;">
                    <div class="collapse-content">
                        ${rentalBanner}
                        <div class="purchase-types purchase-types-CMRP">
                            ${print}
                        </div>
                    </div>
                </div>
            </div>
            <div class="delivery-notice" data-format-specific="digital" style="display: none;">
                <span id="digital-fee-notice">*Digital delivery fee of $<span class="digital-fee-amount">5.99</span> may apply</span> | 
                <button type="button" class="requirements-link" onclick="openRequirementsDialog()">
                    Requirements
                </button>
            </div>
            </div>
        </div>
        <div class="material-notify-container" style="display: none;">
            <!-- Notify me button will be added here when needed -->
        </div>`;
    } 

    const details = this._extractProductDetails(product);
    const detailsData = this._generateDetailsHTML(details);
    
    const isAdoptedTitle = product.tags && product.tags.includes("adoptedsupply");
    const priceRange = this.getPriceRangeForProducts(product);
    
    const authorBlock =
      authorName && String(authorName).trim()
        ? `<div class="material-author choice-product-card__author">${authorName}</div>`
        : `<div class="material-author choice-product-card__author choice-product-card__author--empty" aria-hidden="true">&nbsp;</div>`;
    const isbnBlock =
      isbn !== 'Not specified' && isAdoptedTitle === false
        ? `<div class="choice-meta-row"><span class="choice-meta-label">ISBN:</span><span class="choice-meta-value">${isbn}</span></div>`
        : '';
    const editionBlock =
      edition !== 'Not specified'
        ? `<div class="choice-meta-row"><span class="choice-meta-label">Edition:</span><span class="choice-meta-value">${edition}</span></div>`
        : '';

    return `
      <div class="material-item choice-material-item choice-product-card" >
        <div class="choice-product-card__inner">
        <div class="material-information" onclick="handleMaterialItemClick(event)">
            <div class="material-content choice-product-card__content">
                <div class="choice-product-card__image-wrap">
                    <img src="${product.image}" alt="${product.title}" class="material-image" />
                    ${this.materialPdpLinkHTML(product.url)}
                </div>
                <div class="material-text-content choice-product-card__text">
                    <div class="material-text">
                        <div class="choice-product-card__title-row">
                        <h3 class="material-title choice-product-card__title">${product.title}</h3>
                        <div class="choice-product-card__aside">
                    <button type="button" class="choice-product-card__plus-select choice-plus-button-${sectionData.courseInfo.selectedsectionId}" id="plus-box-product-${product.id}-${instanceId}" data-product-id="${product.id}" aria-expanded="false" aria-label="Open purchase options">
                        <span class="choice-plus__icon" aria-hidden="true">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        </span>
                        <span class="choice-plus__count" aria-hidden="true" hidden></span>
                    </button>
        <div class="choice-price-material">
          <div class="check-price-material">
            <h3 class="material-title " id="price-range-choice-${product.id}">${priceRange.priceRange}</h3>
            <h3 class="material-title hide-price-choice" id="price-choice-${product.id}"></h3>
            <div class="purchase-choice-checkbox-container" onclick="event.stopPropagation()">
              <input type="checkbox" id="check-box-product-${product.id}" class="purchase-choice-checkbox view-check-btn-choice" data-section-id="${sectionData.courseInfo.selectedsectionId}">
              <label for="check-box-product-${product.id}" class="checkbox-label" style="display: inline-block;"></label>
            </div>
          </div>
          <div class="material-view-more material-view-more-choice" id="div-view-details-${product.id}">
            <button class="view-more-btn-choice" type="button" id="choice-info-product-${product.id}">
              <span>View details</span>
                  <svg class="chevron-down" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"></path>
                  </svg>
              </button>
          </div>
        </div>
        </div>
                        </div>
                        <div class="material-details choice-product-card__details">
                            ${authorBlock}
                            ${isbnBlock}
                            ${editionBlock}
                            <div class="material-view-options choice-product-card__view-options" onclick="togglePurchaseOptions(this)">
                                <span>View options</span>
                                <svg class="chevron-right" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </div>
      </div>
      <div class="material-item choice-info choice-hide" id="choice-details-product-${product.id}" role="presentation">
        <div class="choice-drawer-backdrop" aria-hidden="true"></div>
        <div class="choice-drawer-sheet" role="dialog" aria-modal="true" aria-labelledby="choice-drawer-title-${product.id}">
          <div class="choice-drawer-header">
            <span class="choice-drawer-title" id="choice-drawer-title-${product.id}">Choose purchase options</span>
            <button type="button" class="choice-drawer-close" aria-label="Close">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M5 5l8 8M13 5l-8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <div class="choice-drawer-scroll-clip">
          <div class="choice-drawer-scroll">
            <div class="material-information" onclick="handleMaterialItemClick(event)">
              <div class="material-content">
                <div class="material-text-content">
                  <div class="material-text">
                    <div class="material-details">
                      <div class="aditional-info-title">Additional Textbook Details</div>
                      ${detailsData !== '' ? detailsData : ''}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="material-purchase-options">
              <div class="aditional-purchase-title">Choose a format to see purchase options</div>
              ${htmlOutput}
            </div>
          </div>
          </div>
        </div>
      </div>
    `;
  }
   /**
   * Create a material item element
   * @param {any} product - Product data
   * @returns {HTMLElement} Material item element
   * @private
   */
  // @ts-ignore
  async createMaterialItemChoice(product, sectionData, instanceId = '') {
    const materialItem = document.createElement('div');
    materialItem.className = 'material-item';
    materialItem.classList.add('choice-item');
    if (instanceId) materialItem.dataset.purchaseInstanceId = instanceId;

    materialItem.innerHTML = await this.renderMaterialItemTemplateChoice(
      product,
      sectionData,
      instanceId
    );

    return materialItem;
  }
  /**
   * Create a material item element
   * @param {any} product - Product data
   * @returns {HTMLElement} Material item element
   * @private
   */
  // @ts-ignore
  async createMaterialItem(product, sectionData, instanceId = '', options = {}) {
    const childProducts = options.childProducts || [];
    const isBundle = childProducts.length > 0;

    const materialItem = document.createElement('div');
    materialItem.className = 'material-item' + (isBundle ? ' material-item--bundle' : '');
    if (instanceId) {
      materialItem.dataset.purchaseInstanceId = instanceId;
    } else {
      const sectionId = sectionData?.courseInfo?.selectedsectionId || '';
      if (sectionId) materialItem.dataset.purchaseInstanceId = sectionId;
    }
    if (isBundle && childProducts.length) {
      materialItem.dataset.bundleChildIds = childProducts.map((p) => String(p.id)).join(',');
      materialItem.dataset.cmrpBundleParent = JSON.stringify(product);
      materialItem.dataset.cmrpBundleProducts = JSON.stringify(childProducts);
    }

    if (!isBundle) {
      // Preserve legacy DOM structure for standard CMRP items so existing layout/CSS remains unchanged.
      materialItem.innerHTML = await this.renderMaterialItemTemplate(product, sectionData, instanceId);
      return materialItem;
    }

    const mainWrapper = document.createElement('div');
    mainWrapper.className = 'material-item-main';
    mainWrapper.innerHTML = await this.renderMaterialItemTemplate(product, sectionData, instanceId);
    materialItem.appendChild(mainWrapper);

    if (isBundle) {
      const isMobile = window.innerWidth <= 786;

      const desktopToggleIconSvg = '<svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.71127 0.0771405C4.89319 -0.0428264 5.14076 -0.0229108 5.30087 0.137199L9.80087 4.6372L9.86093 4.71117C9.98089 4.8931 9.96098 5.14066 9.80087 5.30077C9.64076 5.46088 9.39319 5.4808 9.21127 5.36083L9.13729 5.30077L4.96908 1.13256L0.800868 5.30077C0.61781 5.48383 0.320352 5.48383 0.137294 5.30077C-0.0457646 5.11772 -0.0457646 4.82026 0.137294 4.6372L4.63729 0.137199L4.71127 0.0771405Z" fill="#141515"/></svg>';

      const computeBundlePriceRange = (products) => {
        if (!Array.isArray(products) || products.length === 0) return null;
        const prices = [];
        products.forEach((p) => {
          if (!p || !Array.isArray(p.variants)) return;
          p.variants.forEach((variant) => {
            if (!variant) return;
            const priceNumber = parseFloat(variant.price);
            if (!Number.isNaN(priceNumber)) prices.push(priceNumber);
          });
        });
        if (prices.length === 0) return null;
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        const format = (value) => `$${value.toFixed(2)}`;
        if (min === max) return format(min);
        return `${format(min)} - ${format(max)}`;
      };

      const bundlePriceRange = computeBundlePriceRange(childProducts);

      const toggleButton = document.createElement('button');
      toggleButton.type = 'button';
      toggleButton.className = 'bundle-items-toggle';
      if (isMobile) {
        toggleButton.innerHTML = `
          <span class="bundle-items-toggle-text">View individual items</span>
          ${bundlePriceRange ? `<span class="bundle-items-toggle-price">From ${bundlePriceRange}</span>` : ''}
        `;
      } else {
        toggleButton.innerHTML = `
          <span class="bundle-items-toggle-prefix">Prefer to shop items separately?</span>
          <span class="bundle-items-toggle-text">View Items</span>
          <span class="bundle-items-toggle-icon">${desktopToggleIconSvg}</span>
        `;
      }

      if (isMobile) {
        materialItem.appendChild(toggleButton);
      } else {
        const purchaseOptionsForToggle = mainWrapper.querySelector('.material-purchase-options') || materialItem;
        purchaseOptionsForToggle.appendChild(toggleButton);
      }

      let bundleComponent = null;
      let desktopOpen = false;
      let mobileOpen = false;

      const updateDesktopToggleLabel = () => {
        if (isMobile) return;
        const textEl = toggleButton.querySelector('.bundle-items-toggle-text');
        if (!textEl) return;
        textEl.textContent = desktopOpen ? 'Hide items' : 'View Items';
      };

      toggleButton.addEventListener('click', () => {
        if (isMobile) {
          if (!mobileOpen) {
            if (!bundleComponent) {
              bundleComponent = document.createElement('bundle-products-component');
              const bundleChildJson = JSON.stringify(childProducts);
              bundleComponent.setAttribute('products-data', bundleChildJson);
              bundleComponent.setAttribute('section-data', JSON.stringify(sectionData));
              bundleComponent.setAttribute('view-mode', 'mobile-drawer');
              bundleComponent.dataset.cmrpProducts = bundleChildJson;
              materialItem.appendChild(bundleComponent);

              bundleComponent.addEventListener('bundleDrawerClosed', () => {
                if (bundleComponent && materialItem.contains(bundleComponent)) {
                  materialItem.removeChild(bundleComponent);
                }
                bundleComponent = null;
                mobileOpen = false;
                toggleButton.classList.remove('bundle-items-toggle--open');
              });
            }
            mobileOpen = true;
            toggleButton.classList.add('bundle-items-toggle--open');
          } else {
            if (bundleComponent) {
              materialItem.removeChild(bundleComponent);
              bundleComponent = null;
            }
            mobileOpen = false;
            toggleButton.classList.remove('bundle-items-toggle--open');
          }
        } else {
          if (!bundleComponent) {
            bundleComponent = document.createElement('bundle-products-component');
            const bundleChildJson = JSON.stringify(childProducts);
            bundleComponent.setAttribute('products-data', bundleChildJson);
            bundleComponent.setAttribute('section-data', JSON.stringify(sectionData));
            bundleComponent.setAttribute('view-mode', 'inline');
            bundleComponent.dataset.cmrpProducts = bundleChildJson;
            materialItem.appendChild(bundleComponent);
            desktopOpen = true;
          } else {
            desktopOpen = !desktopOpen;
            bundleComponent.style.display = desktopOpen ? '' : 'none';
          }
          toggleButton.classList.toggle('bundle-items-toggle--open', desktopOpen);
          updateDesktopToggleLabel();
        }
      });

      if (!isMobile) {
        updateDesktopToggleLabel();
      }
    }

    return materialItem;
  }
  _extractProductDetails(product) {
    return {
      'Publisher':
        this.productHandler?.extractPublisher?.(product) || 'Not specified',
      'Format':
        this.productHandler?.extractFormat?.(product) || 'Not specified',
      'Copyright':
        this.productHandler?.extractCopyRight?.(product) || 'Not specified',
      'Publication':
        this.productHandler?.extractPublication?.(product) || 'Not specified'
    };
  }
  _generateDetailsHTML(details) {
    const rows = Object.entries(details)
      .map(
        ([key, value]) =>
          value === "Not specified" 
            ? ''  // Retorna uma string vazia se o valor for "Not Specficated"
            : `<div class="detail-row">${key}: <span class="detail-value">${value}</span></div>`
      )
      .join('');
   
    
    return rows;
  }

  /**
   * PDP shortcut on card image (bottom-right). SVG matches assets/cmrp-pdp-link-icon.svg.
   * @param {string} [productUrl]
   * @returns {string}
   */
  materialPdpLinkHTML(productUrl) {
    const href = String(productUrl || '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;');
    return `<a href="${href}" class="material-image-pdp-link" aria-label="View product details" onclick="event.stopPropagation()" rel="noopener noreferrer"><svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><rect x="0.5" y="0.5" width="39" height="39" rx="19.5" fill="white"/><rect x="0.5" y="0.5" width="39" height="39" rx="19.5" stroke="#2B2B2B" stroke-linecap="round"/><path fill-rule="evenodd" clip-rule="evenodd" d="M20.75 15.125C20.75 13.6753 21.9253 12.5 23.375 12.5C24.8247 12.5 26 13.6753 26 15.125C26 16.5747 24.8247 17.75 23.375 17.75C22.6985 17.75 22.0818 17.4941 21.6163 17.0738L17.7289 19.6655C17.7428 19.775 17.75 19.8867 17.75 20C17.75 20.1133 17.7428 20.225 17.7289 20.3345L21.6163 22.9262C22.0818 22.5059 22.6985 22.25 23.375 22.25C24.8247 22.25 26 23.4253 26 24.875C26 26.3247 24.8247 27.5 23.375 27.5C21.9253 27.5 20.75 26.3247 20.75 24.875C20.75 24.6446 20.7797 24.4212 20.8354 24.2083L17.1062 21.7222C16.6249 22.2753 15.9158 22.625 15.125 22.625C13.6753 22.625 12.5 21.4497 12.5 20C12.5 18.5503 13.6753 17.375 15.125 17.375C15.9158 17.375 16.6249 17.7247 17.1062 18.2778L20.8354 15.7917C20.7797 15.5788 20.75 15.3554 20.75 15.125ZM23.375 14C22.7537 14 22.25 14.5037 22.25 15.125C22.25 15.7463 22.7537 16.25 23.375 16.25C23.9963 16.25 24.5 15.7463 24.5 15.125C24.5 14.5037 23.9963 14 23.375 14ZM15.125 18.875C14.5037 18.875 14 19.3787 14 20C14 20.6213 14.5037 21.125 15.125 21.125C15.7463 21.125 16.25 20.6213 16.25 20C16.25 19.3787 15.7463 18.875 15.125 18.875ZM22.25 24.875C22.25 24.2537 22.7537 23.75 23.375 23.75C23.9963 23.75 24.5 24.2537 24.5 24.875C24.5 25.4963 23.9963 26 23.375 26C22.7537 26 22.25 25.4963 22.25 24.875Z" fill="#2B2B2B"/></svg></a>`;
  }

  /**
   * Render material item template (mimics Liquid snippet)
   * @param {any} product - Product data
   * @returns {string} HTML string
   * @private
   */
  // @ts-ignore
  async renderMaterialItemTemplate(product,sectionData) {
    // Extract product data
    const hasMaterialType =
      product.material_type && product.material_type.trim() !== '';
    const badgeClass = hasMaterialType
      ? product.material_type.toLowerCase()
      : '';
    const badgeText = hasMaterialType
      ? product.material_type.toUpperCase()
      : '';
    const authorName = this.productHandler.extractAuthorName(product) || '';
    const edition = this.productHandler.extractEditionFromTitle(product) || 'Not specified';
    const isbn = this.productHandler.extractISBNFromProduct(product) || 'Not specified';
    // @ts-ignore
    const formatButtons = this.productHandler.generateFormatButtons(product);
    //console.log("formatButtons ==>", formatButtons)
    //console.log("product ==>", product)
    const prod_formats = this.productHandler.getVariantUniques(product);
    //console.log("prod_formats ==>", prod_formats)

    const cm = product?.metafields?.course_materials || product?.metafields?.course_materials?.value;
    const isBundleParent =
      cm?.is_package_flag === true || String(cm?.is_package_flag).toLowerCase() === 'true';
    const bundleCourseMessageHtml = isBundleParent
      ? '<p class="bundle-course-message">This course uses a bundle chosen by your instructor.</p>'
      : '';

    // Badge HTML (conditional)
    let bagdeHtmlData = ''
    if(hasMaterialType){
      if(badgeClass =="choice" || badgeClass =="CHOICE" || badgeClass =="choice/required" || badgeClass =="CHOICE/REQUIRED"){
        bagdeHtmlData =  `
          <div class="material-badge-choice">
            <div class="material-badge required">
                <span class="badge-text">REQUIRED</span>
            </div>
            <div class="material-badge choice">
                <span class="badge-text">CHOICE</span>
            </div>
          </div>
        `
      }else if(badgeClass =="choice/recommended"){
        bagdeHtmlData =  `
          <div class="material-badge-choice">
            <div class="material-badge recommended">
                <span class="badge-text">RECOMMENDED</span>
            </div>
            <div class="material-badge choice">
                <span class="badge-text">CHOICE</span>
            </div>
          </div>
        `
      }else if(badgeText == "SUGGESTED"){
        bagdeHtmlData =  `
            <div class="material-badge recommended">
                <span class="badge-text">${badgeText}</span>
            </div>
        `
      }else{
        bagdeHtmlData =  `
            <div class="material-badge ${badgeClass}">
                <span class="badge-text">${badgeText}</span>
            </div>
        `
      }
    }
    const badgeHTML = hasMaterialType
      ? bagdeHtmlData
      : '';

    let htmlOutput;

    const showDigitalDeliveryNotice =
      await ProductDisplayManager.getDigitalDeliveryNoticeVisible();
    const digitalDeliveryNoticeHtml = showDigitalDeliveryNotice
      ? '<div class="digital-delivery-notice">*Digital delivery fees may apply</div>'
      : '';

    // Check if product has rental options
    const hasRentalOptions = this.hasRentalOptions(product);

    const sectionIdNumber = sectionData.courseInfo.selectedsectionId;
    //console.log("sectionIdNumber", sectionIdNumber)

    const filteredSectionAccess = window.courseData.ddcs_section.find(
      section => section.section_id === sectionData.courseInfo.selectedsectionId
    );

    
    const terms = Array.isArray(window.courseData?.ddcs_term)
      ? window.courseData.ddcs_term
      : [];
    const campuses = Array.isArray(window.courseData?.ddcs_campus_entity)
      ? window.courseData.ddcs_campus_entity
      : [];
    const filteredTermSelected = terms.find(
      term => term.term_id === sectionData.courseInfo.termId
    );
    const campusEntityId =
      filteredTermSelected?.campus_entity_id ||
      filteredSectionAccess?.campus_entity_id ||
      '';
    const filteredAccessProgramName = campuses.find(
      campus => campus.campus_entity_id === campusEntityId
    );

    
    const includedProgramName =
      typeof filteredAccessProgramName?.included_program_name === 'string'
        ? filteredAccessProgramName.included_program_name.trim()
        : '';
    const accessProgramNote = includedProgramName || filteredSectionAccess?.included_section_note;
    
    let mapped_section = null;
    
    // Validate and parse section_data_mapping
    if (product.metafields?.course_materials?.section_data_mapping) {
        try {
            const section_data_mapping = JSON.parse(product.metafields.course_materials.section_data_mapping);
            //console.log("section_data_mapping", section_data_mapping);
            
            // Check if it's an array with values
            if (Array.isArray(section_data_mapping) && section_data_mapping.length > 0) {
                // Find element with SectionID equal to sectionIdNumber
                mapped_section = section_data_mapping.find(item => item.SectionID === sectionIdNumber);
                
            }
        } catch (error) {
            console.error("Error parsing section_data_mapping:", error);
        }
    }

    //console.log("mapped_section", mapped_section);
            
    // Check if mapped_section exists and IncludEDMaterialFlag is true
    const accessProgramBanner = (mapped_section && (mapped_section.IncludEDMaterialFlag === "true" || mapped_section.IncludEDMaterialFlag === true)) 
      ? `<div class="rental-banner access-program-style">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path fill-rule="evenodd" clip-rule="evenodd" d="M8.03751 3.23346C7.81155 3.23346 7.61727 3.41091 7.61727 3.6432V3.9899H7.49033C7.1739 3.9899 6.8716 4.11407 6.64959 4.33328C6.42775 4.55234 6.30402 4.84839 6.30402 5.15606C6.30402 5.46373 6.42775 5.75978 6.64959 5.97884C6.8716 6.19806 7.1739 6.32223 7.49033 6.32223H7.61727V7.01562H6.72426C6.4983 7.01562 6.30402 7.19307 6.30402 7.42536C6.30402 7.65765 6.4983 7.83509 6.72426 7.83509H7.61727V8.18179C7.61727 8.41408 7.81155 8.59153 8.03751 8.59153C8.26348 8.59153 8.45775 8.41408 8.45775 8.18179V7.83509H8.5847C8.90113 7.83509 9.20342 7.71092 9.42543 7.4917C9.64728 7.27265 9.771 6.9766 9.771 6.66893C9.771 6.36126 9.64728 6.06521 9.42543 5.84615C9.20342 5.62693 8.90113 5.50276 8.5847 5.50276H8.45775V4.80936H9.13189C9.35786 4.80936 9.55213 4.63192 9.55213 4.39963C9.55213 4.16734 9.35786 3.9899 9.13189 3.9899H8.45775V3.6432C8.45775 3.41091 8.26348 3.23346 8.03751 3.23346ZM7.24768 4.90908C7.31298 4.84461 7.40037 4.80936 7.49033 4.80936H7.61727V5.50276H7.49033C7.40037 5.50276 7.31298 5.46752 7.24768 5.40304C7.18218 5.33836 7.1445 5.2496 7.1445 5.15606C7.1445 5.06252 7.18218 4.97376 7.24768 4.90908ZM8.45775 7.01562V6.32223H8.5847C8.67465 6.32223 8.76205 6.35747 8.82734 6.42195C8.89285 6.48663 8.93052 6.57539 8.93052 6.66893C8.93052 6.76247 8.89285 6.85123 8.82734 6.91591C8.76205 6.98038 8.67465 7.01562 8.5847 7.01562H8.45775Z" fill="#204A65"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.61333 12.6985C9.61333 12.9741 9.39751 13.1976 9.13129 13.1976H6.85164C6.58542 13.1976 6.3696 12.9741 6.3696 12.6985C6.3696 12.4229 6.58542 12.1995 6.85164 12.1995H9.13129C9.39751 12.1995 9.61333 12.4229 9.61333 12.6985Z" fill="#204A65"/>
        <path fill-rule="evenodd" clip-rule="evenodd" d="M9.18567 9.08436L9.68398 8.7961C10.6952 8.21114 11.3719 7.12026 11.3719 5.8725C11.3719 4.00885 9.86114 2.49807 7.9975 2.49807C6.13385 2.49807 4.62307 4.00885 4.62307 5.8725C4.62307 7.12026 5.29978 8.21114 6.31101 8.7961L6.80932 9.08435V10.6094C6.80932 10.6619 6.85187 10.7044 6.90437 10.7044H9.09062C9.14312 10.7044 9.18567 10.6619 9.18567 10.6094V9.08436ZM10.1837 9.66004V10.6094C10.1837 11.2131 9.69433 11.7025 9.09062 11.7025H6.90437C6.30065 11.7025 5.81125 11.2131 5.81125 10.6094V9.66003C4.50431 8.90401 3.625 7.49094 3.625 5.8725C3.625 3.45763 5.58263 1.5 7.9975 1.5C10.4124 1.5 12.37 3.45763 12.37 5.8725C12.37 7.49094 11.4907 8.90401 10.1837 9.66004Z" fill="#204A65"/>
      </svg>
      <span class="access-program-text-product">
        ${accessProgramNote || 'Access Program'} material. No purchase needed unless you opt out.
      </span>                   
    </div>` 
    : '';

    if(product.hideSalesChannel && product.variants.length === 0){
      htmlOutput = `${accessProgramBanner}`;
    }else{
      if (prod_formats.length === 1) {
          if (prod_formats[0] === "Digital") {
              const initialPurchaseOptions = await this.productHandler.generateInitialPurchaseOptions("Digital", product, sectionData);
              const rentalBanner = hasRentalOptions ? `
                          <div class="rental-banner">
                              <span class="dollar-icon">$</span>
                              Rent for the semester and save money!
                          </div>` : '';
              const digitalPrice = this.getLowestPriceForFormat(product, "Digital");
              htmlOutput = `
          ${accessProgramBanner}
            ${product.hideSalesChannel ? `` : `
              <div class="purchase-options-container">
                ${MULTI_FORMAT_SELECTION_WARNING_HTML}
                <div class="format-tabs">
                    <button class="format-tab active" data-format="digital" onclick="window.toggleFormatCollapse(this)">
                        <span class="format-tab-title">Digital</span>
                        <span class="format-tab-price">${digitalPrice}</span>
                    </button>
                </div>
                <div class="format-content-area">
                    <div class="format-collapse active" data-format="digital" style="display: block;">
                        <div class="collapse-content">
                            <div class="purchase-types">
                                ${initialPurchaseOptions}
                            </div>
                            ${rentalBanner}
                            ${digitalDeliveryNoticeHtml}
                        </div>
                    </div>
                </div>
                
            </div>
            <div class="material-notify-container" style="display: none;">
                <!-- Notify me button will be added here when needed -->
            </div>
            `}
          `;
          } else if (prod_formats[0] === "Print") {
              const print = await this.productHandler.generateInitialPurchaseOptions("Print", product, sectionData);
              const rentalBanner = hasRentalOptions ? `
                          <div class="rental-banner">
                              <span class="dollar-icon">$</span>
                              Rent for the semester and save money!
                          </div>` : '';
              const printPrice = this.getLowestPriceForFormat(product, "Print");
              htmlOutput = `
          ${accessProgramBanner}
          <div class="purchase-options-container">
              ${MULTI_FORMAT_SELECTION_WARNING_HTML}
              <div class="format-tabs">
                  <button class="format-tab active" data-format="print" onclick="window.toggleFormatCollapse(this)">
                      <span class="format-tab-title">Print</span>
                      <span class="format-tab-price">${printPrice}</span>
                  </button>
              </div>
              <div class="format-content-area">
                  <div class="format-collapse active" data-format="print" style="display: block;">
                      <div class="collapse-content">
                          <div class="purchase-types">
                              ${print}
                          </div>
                          ${rentalBanner}
                      </div>
                  </div>
              </div>
          </div>
          <div class="material-notify-container" style="display: none;">
              <!-- Notify me button will be added here when needed -->
          </div>`;
          } else {
            const initialPurchaseOptions = await this.productHandler.generateInitialPurchaseOptions("", product, sectionData);
            htmlOutput = `
              <!-- Simple product - direct purchase options -->
              <div class="simple-product-purchase">
                ${accessProgramBanner}
                ${product.hideSalesChannel ? `` : `
                ${MULTI_FORMAT_SELECTION_WARNING_HTML}
                <div class="format-content-area">
                    <div class="format-collapse active" style="display: block;">
                        <div class="collapse-content">
                            <div class="purchase-types">
                              ${initialPurchaseOptions}
                            </div>
                        </div>
                    </div>
                </div>
              </div>
              <div class="material-notify-container" style="display: none;">
                <!-- Notify me button will be added here when needed -->
              </div>
                `}
              </div>
            `;
          }
      } else if (prod_formats.length === 2 && prod_formats.includes("Digital") && prod_formats.includes("Print")) {
          const digital = await this.productHandler.generateInitialPurchaseOptions("Digital", product, sectionData);
          const print = await this.productHandler.generateInitialPurchaseOptions("Print", product, sectionData);
          const rentalBanner = hasRentalOptions ? `
                          <div class="rental-banner">
                              <span class="dollar-icon">$</span>
                              Rent for the semester and save money!
                          </div>` : '';
          const digitalPrice = this.getLowestPriceForFormat(product, "Digital");
          const printPrice = this.getLowestPriceForFormat(product, "Print");
          htmlOutput = `
          ${accessProgramBanner}
          <div class="purchase-options-container">
              ${MULTI_FORMAT_SELECTION_WARNING_HTML}
              <h3 class="purchase-options-title">Choose a format to see purchase options</h3>
              <div class="format-tabs">
                ${product.hideSalesChannel ? `` : `
                  <button class="format-tab active" data-format="digital" onclick="window.toggleFormatCollapse(this)">
                      <span class="format-tab-title">Digital</span>
                      <span class="format-tab-price">${digitalPrice}</span>
                  </button>
                `}
                  <button class="format-tab ${product.hideSalesChannel ? 'active' : ''}" data-format="print" onclick="window.toggleFormatCollapse(this)">
                      <span class="format-tab-title">Print</span>
                      <span class="format-tab-price">${printPrice}</span>
                  </button>
              </div>
              <div class="format-content-area">
                ${product.hideSalesChannel ? `` : `
                  <div class="format-collapse active" data-format="digital" style="display: block;">
                      <div class="collapse-content">
                          <div class="purchase-types">
                              ${digital}
                          </div>
                          ${rentalBanner}
                          ${digitalDeliveryNoticeHtml}
                      </div>
                  </div>
                  `}
                  <div class="format-collapse" data-format="print" style="${product.hideSalesChannel ? 'display: block;' : 'display: none;'}">
                      <div class="collapse-content">
                          <div class="purchase-types">
                              ${print}
                          </div>
                          ${rentalBanner}
                      </div>
                  </div>
              </div>
              ${product.hideSalesChannel ? `` : `
              <div class="delivery-notice" data-format-specific="digital" style="display: none;">
                  <span id="digital-fee-notice">*Digital delivery fee of $<span class="digital-fee-amount">5.99</span> may apply</span> | 
                  <button type="button" class="requirements-link" onclick="openRequirementsDialog()">
                      Requirements
                  </button>
              </div>
              </div>
              `}
          </div>
          <div class="material-notify-container" style="display: none;">
              <!-- Notify me button will be added here when needed -->
          </div>`;
      } 
    }

    const details = this._extractProductDetails(product);
    const detailsData = this._generateDetailsHTML(details);
    const isAdoptedTitle = product.tags && product.tags.includes("adoptedsupply");
    
    // Main template for material item rendering - MATH 100 style
    return `
        <div class="material-information" onclick="handleMaterialItemClick(event)">
            <div class="material-content">
                <div class="material-image-container">
                    <img src="${product.image}" alt="${product.title}" class="material-image" />
                    ${this.materialPdpLinkHTML(product.url)}
                    <button class="material-image-plus-button" type="button" onclick="togglePurchaseOptions(this)">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M8 3.5V12.5M3.5 8H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                </div>
                <div class="material-text-content">
                    <div class="material-text">
                        ${badgeHTML}
                        <h3 class="material-title">${product.title}</h3>
                        <div class="material-details">
                            <div class="material-author">${authorName || ''}</div>
                            ${edition !== "Not specified" ? `<div class="material-edition">Edition: ${edition}</div>` : ''}
                            ${isbn !== "Not specified" && isAdoptedTitle === false ? `<div class="material-isbn">ISBN: ${isbn}</div>` : ''}
                            ${detailsData !== "" ? `<div class="material-view-more">
                                <button class="view-more-btn" type="button">
                                    <span>View more details</span>
                                    <svg class="chevron-down" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M4 6L8 10L12 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </button>
                            </div>`: ''}
                            
                            <div class="material-view-options" onclick="togglePurchaseOptions(this)">
                                <span>View options</span>
                                <svg class="chevron-right" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M6 4L10 8L6 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="material-purchase-options">
            ${bundleCourseMessageHtml}
            ${htmlOutput}
        </div>
        </div>
    `;
  }

  /**
   * Get adoption status info based on note content
   * @param {string} note - Adoption status note
   * @returns {Object} Status info with type and icon
   */
  getAdoptionStatusInfo(note) {
    const lowerNote = note.toLowerCase();

    if (!this.ADOPTION_STATUS_CONFIG)
      return { type: 'pending', icon: this.ADOPTION_STATUS_ICON };

    for (const [statusType, config] of Object.entries(
      this.ADOPTION_STATUS_CONFIG
    )) {
      if (config.keywords.some(keyword => lowerNote.includes(keyword))) {
        return { type: statusType, icon: this.ADOPTION_STATUS_ICON };
      }
    }

    return { type: 'pending', icon: this.ADOPTION_STATUS_ICON };
  }

  /**
   * Create adoption card HTML (mimics snippets/adoption-card.liquid)
   * @param {any} filter - Filter object
   * @param {any} section - Section object
   * @param {string} courseCode - Course code (unused but kept for compatibility)
   * @param {string} note - Adoption note
   * @param {any} statusInfo - Status info
   * @returns {string} HTML string for adoption card
   */
  // @ts-ignore
  // @ts-ignore
  // @ts-ignore
  createAdoptionCardHTML(filter, section, courseCode, note, statusInfo) {
    const instructorName = section
      ? `${section.instructor_first_name || ''} ${
          section.instructor_last_name || ''
        }`.trim() || 'TBA'
      : 'TBA';
    const sectionNumber = section?.display_name || filter.selectedsection;

    // Template follows snippets/adoption-card.liquid structure
    return `
        <div class="course-card adoption-card" data-status="${statusInfo.type}">
            <div class="course-header">
                <div class="header-content">
                    <div class="course-details">
                        <div class="course-title">${filter.selectedDepartment} ${filter.selectedcourse}</div>
                        <div class="section-info">Section: ${sectionNumber} | Instructor: ${instructorName}</div>
                    </div>
                    <div class="header-actions">
                        <button class="remove-btn" onclick="window.removeCourse(this)" 
                                data-division="${filter.selectedDivision || ''}"
                                data-department="${filter.selectedDepartment}" 
                                data-course="${filter.selectedcourse}" 
                                data-section="${filter.selectedsection}">
                            Remove
                            <svg class="remove-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M13.5 4.5L4.5 13.5M4.5 4.5L13.5 13.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
            <div class="adoption-message-content">
                <div class="adoption-icon">${statusInfo.icon}</div>
                <p class="adoption-note">${note}</p>
            </div>
        </div>
    `;
  }

  /**
   * Utility methods for state management (delegated to external functions)
   */
  showEmptyState() {
    /** @type {any} */
    const globalWindow = window;
    if (typeof globalWindow.showEmptyState === 'function') {
      globalWindow.showEmptyState();
    }
  }

  hideEmptyState() {
    /** @type {any} */
    const globalWindow = window;
    if (typeof globalWindow.hideEmptyState === 'function') {
      globalWindow.hideEmptyState();
    }
  }

  hideLoadingState() {
    /** @type {any} */
    const globalWindow = window;
    if (typeof globalWindow.hideLoadingState === 'function') {
      globalWindow.hideLoadingState();
    }
  }

  async handleEmptyFiltersState() {
    const hasCourseBuilderForm = Boolean(
      document.querySelector('.cmrp-filter-form--course-builder'),
    );
    let campusSelect;
    let termSelect;
    if (
      hasCourseBuilderForm &&
      typeof window.cmrpCourseBuilderCampusTermEl === 'function'
    ) {
      campusSelect = window.cmrpCourseBuilderCampusTermEl('campus');
      termSelect = window.cmrpCourseBuilderCampusTermEl('term');
    } else {
      campusSelect =
        typeof window.cmrpQueryDesktopCampusTerm === 'function'
          ? window.cmrpQueryDesktopCampusTerm('campus')
          : document.querySelector('#campus-term-dropdowns #campus');
      termSelect =
        typeof window.cmrpQueryDesktopCampusTerm === 'function'
          ? window.cmrpQueryDesktopCampusTerm('term')
          : document.querySelector('#campus-term-dropdowns #term');
    }

    if (campusSelect?.value && termSelect?.value) {
      if (typeof window.courseMaterialsSidebar?.resetCourseBuilderLineKeepCampusTerm === 'function') {
        try {
          await window.courseMaterialsSidebar.resetCourseBuilderLineKeepCampusTerm();
        } catch (e) {
          console.error('[CMRP] handleEmptyFiltersState reset failed', e);
        }
      }
    } else if (!campusSelect?.value) {
      if (window.courseMaterialsSidebar) {
        window.courseMaterialsSidebar.populateCampus();
      }
    }
  }

  /**
   * Update In Bag indicators for the current section
   * @returns {void}
   */
  updateInBagIndicatorsForSection() {
    const manager = /** @type {any} */ (window)
      .courseMaterialsInteractionManager;

    if (manager && typeof manager.updateAllInBagIndicators === 'function') {
      // Small delay to ensure DOM is fully rendered
      setTimeout(() => {
        manager.updateAllInBagIndicators();
      }, 100);
    }
  }

  /**
   * Theme setting: SAM SKUs (variant SKU must match). Used to hide placeholder materials.
   * @returns {Set<string>}
   */
  static getCmrpSamSkuSet() {
    const raw =
      typeof window !== 'undefined' ? window.shopifySettings?.cmrpSamSkus : null;
    if (!Array.isArray(raw) || raw.length === 0) return new Set();
    return new Set(raw.map(s => String(s).trim()).filter(Boolean));
  }

  /**
   * Theme setting: message when only SAM SKU material(s) apply (replaces generic empty / 400 copy).
   * @returns {string}
   */
  static getCmrpSamHiddenMessage() {
    const g = typeof window !== 'undefined' ? window.shopifySettings : null;
    const msg =
      g && typeof g.cmrpSamSkuHiddenMessage === 'string'
        ? g.cmrpSamSkuHiddenMessage.trim()
        : '';
    return msg || 'No materials required from the bookstore.';
  }

  /**
   * @param {any} product
   * @param {Set<string>} samSet
   * @returns {boolean}
   */
  static productHasSamSku(product, samSet) {
    const set = samSet || ProductDisplayManager.getCmrpSamSkuSet();
    if (!set.size || !product?.variants?.length) return false;
    for (const v of product.variants) {
      const sku = String(v?.sku ?? '').trim();
      if (sku && set.has(sku)) return true;
    }
    return false;
  }
}

// Global function to toggle purchase options on mobile
// @ts-ignore
window.togglePurchaseOptions = function(button) {
  const materialItem = button.closest('.material-item');
  if (!materialItem) return;
  
  const purchaseOptions = materialItem.querySelector('.material-purchase-options');
  if (!purchaseOptions) return;
  
  const isExpanded = purchaseOptions.classList.contains('show');
  
  if (isExpanded) {
    // Hide options
    purchaseOptions.classList.remove('show');
    button.classList.remove('expanded');
  } else {
    // Show options
    purchaseOptions.classList.add('show');
    button.classList.add('expanded');
  }
};

// Global function to handle material item click on mobile
// @ts-ignore
window.handleMaterialItemClick = function(event) {
  // Only handle on mobile devices
  if (window.innerWidth > 968) return;
  
  // Check if the click target is an interactive element
  const interactiveElements = [
    '.material-image-plus-button',
    '.choice-product-card__plus-select',
    '.material-view-options', 
    '.view-more-btn',
    '.purchase-checkbox',
    '.requirements-link',
    '.format-tab',
    '.purchase-type',
    '.checkbox-label'
  ];
  
  const isInteractiveElement = interactiveElements.some(selector => 
    event.target.closest(selector)
  );
  
  if (isInteractiveElement) return;
  
  // Prevent default behavior
  event.preventDefault();
  
  // Find the material item and toggle purchase options
  const materialItem = event.currentTarget;
  const purchaseOptions = materialItem.querySelector('.material-purchase-options');
  
  if (!purchaseOptions) return;
  
  const isExpanded = purchaseOptions.classList.contains('show');
  
  if (isExpanded) {
    // Hide options
    purchaseOptions.classList.remove('show');
  } else {
    // Show options
    purchaseOptions.classList.add('show');
  }
};

// Export for use in other modules
/** @type {any} */
const globalWindowExport = window;
globalWindowExport.ProductDisplayManager = ProductDisplayManager;

// Create namespace for the system
/** @type {any} */
const globalWindow = window;
if (!globalWindow.ProductDisplay) {
  globalWindow.ProductDisplay = {};
}

globalWindow.ProductDisplay.ProductDisplayManager = ProductDisplayManager;